import React, { useEffect, useRef, useState } from "react";
import toastService from '../services/toastService';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { imageAccess } from "../api";
import { getBackendUrl } from "../config/apiConfig";
import PIIRequestModal from "./PIIRequestModal";
import ProfileImage from "./ProfileImage";
import ImageAccessRequestModal from "./ImageAccessRequestModal";
import ActivationBadge from "./ActivationBadge";
import onlineStatusService from "../services/onlineStatusService";
import L3V3LMatchingTable from "./L3V3LMatchingTable";
import MessageModal from "./MessageModal";
import DeleteButton from "./DeleteButton";
import { onPIIAccessChange } from "../utils/piiAccessEvents";
import { getActivityBadgeProps, getRelativeActivityTime } from "../utils/activityFormatter";
import { generateAboutMe, generatePartnerPreference, generateLookingForSummary } from "../utils/profileDescriptionGenerator";
import ProfileCreatorBadge from "./ProfileCreatorBadge";
import { getWorkingStatus } from "../utils/workStatusHelper";
import RichTextEditor from "./shared/RichTextEditor";
import { getAuthenticatedImageUrl } from "../utils/imageUtils";
import logger from "../utils/logger";
import { getApiErrorMessage, isMembershipRequiredError } from "../utils/membershipAccess";
import { formatFullDateTime, formatRelativeTime, formatCompactDateTime } from "../utils/timeFormatter";
import ActivitySummaryPanel from "./ActivitySummaryPanel";
import { useContribution } from "../contexts/ContributionContext";
import { isNativePlatform } from "../services/biometricAuth";
import "./Profile.css";
import { ACTION_ICONS } from "../constants/icons";
import { createApiInstance } from "../api";
import {
  buildProfileShareMessage,
  buildSmsShareUrl,
  buildWhatsAppShareUrl,
  copyTextToClipboard,
  getProfileShareFallbackUrl
} from "../utils/profileShare";

// Use global API factory for session handling
const verificationApi = createApiInstance();

const Profile = ({ 
  usernameFromProp = null,  // Optional: username passed as prop for embedded mode
  embedded = false,         // Flag for embedded mode (hides header/navigation)
  initialUserData = null    // Optional: Pre-loaded user data to show immediately
}) => {
  const { username: usernameFromParams } = useParams();
  const username = usernameFromProp || usernameFromParams;
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(initialUserData);
  const [loading, setLoading] = useState(!initialUserData);
  const [error, setError] = useState("");
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const actionErrorTimerRef = useRef(null);

  // Toast-style error for transient action failures (auto-dismiss after 5s)
  const showActionError = (msg) => {
    setActionError(msg);
    if (actionErrorTimerRef.current) clearTimeout(actionErrorTimerRef.current);
    actionErrorTimerRef.current = setTimeout(() => setActionError(""), 5000);
  };
  // Contribution prompt state — shared across app via context.
  // shouldShowContribution already factors in persistent dismissal.
  const { shouldShowContribution, openPopup, dismissBanner, needsActivation } = useContribution();
  
  // Search carousel navigation state
  const [searchResults, setSearchResults] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  
  // KPI Stats
  const [kpiStats, setKpiStats] = useState({
    profileViews: 0,
    shortlistedBy: 0,
    favoritedBy: 0
  });

  const [viewerViewMetrics, setViewerViewMetrics] = useState({
    lastViewedAt: null,
    viewCount: 0
  });

  // Track which profiles we've already recorded a view for in this session,
  // so React StrictMode's double-invocation of effects (in dev) doesn't
  // double-count views or overwrite "Last viewed" with the just-recorded view.
  const trackedViewsRef = useRef(new Set());
  
  // Action states (isExcluded and showMessageModal only - others already exist)
  const [isExcluded, setIsExcluded] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  
  // Exclusion preview modal state
  const [showExclusionPreview, setShowExclusionPreview] = useState(false);
  const [exclusionPreviewData, setExclusionPreviewData] = useState(null);
  const [exclusionLoading, setExclusionLoading] = useState(false);
  
  // L3V3L Matching Data
  const [l3v3lMatchData, setL3v3lMatchData] = useState(null);
  const [l3v3lIsQuickScore, setL3v3lIsQuickScore] = useState(false);
  const [l3v3lRefreshing, setL3v3lRefreshing] = useState(false);
  
  // Editable About Me state
  const [isEditingAboutMe, setIsEditingAboutMe] = useState(false);
  const [editedAboutMe, setEditedAboutMe] = useState('');
  const [savingAboutMe, setSavingAboutMe] = useState(false);
  
  // Editable Partner Preference ("What You're Looking For") state
  const [isEditingPartnerPref, setIsEditingPartnerPref] = useState(false);
  const [editedPartnerPref, setEditedPartnerPref] = useState('');
  const [savingPartnerPref, setSavingPartnerPref] = useState(false);
  
  // AI Rephrase state
  const [isRephrasing, setIsRephrasing] = useState(false);
  const [rephraseStyle, setRephraseStyle] = useState('concise');
  const [aiProvider, setAiProvider] = useState('groq'); // 'groq' (recommended) or 'gemini'

  // SMS Share state
  const [shareRecipient, setShareRecipient] = useState('');
  const [sharePhone, setSharePhone] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [currentUserContacts, setCurrentUserContacts] = useState([]);
  const [profileShares, setProfileShares] = useState([]);
  const [showMessageEditor, setShowMessageEditor] = useState(false);
  const [customMessage, setCustomMessage] = useState('');

  // Contact labels matching profile edit
  const CONTACT_LABELS = ['primary', 'secondary', 'self', 'parent', 'spouse', 'daughter', 'son', 'work', 'other'];

  logger.debug('Profile component loaded for:', username);
  
  // Check for status message from ProtectedRoute
  useEffect(() => {
    const message = sessionStorage.getItem('statusMessage');
    if (message) {
      setStatusMessage(message);
      sessionStorage.removeItem('statusMessage');
      // Auto-hide after 10 seconds
      setTimeout(() => setStatusMessage(""), 10000);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const getProfileUpdatedText = (dateValue) => {
    if (!dateValue) return null;
    const relative = formatRelativeTime(dateValue);
    if (!relative || relative === 'Never') return null;
    if (relative === 'Just now') return 'just now';
    if (relative === 'Yesterday') return 'yesterday';
    return relative;
  };
  
  // Extract search carousel context from navigation state
  useEffect(() => {
    if (location.state?.searchResults && location.state?.currentIndex !== null) {
      setSearchResults(location.state.searchResults);
      setCurrentIndex(location.state.currentIndex);
      logger.debug('Carousel context loaded:', {
        totalResults: location.state.searchResults.length,
        currentIndex: location.state.currentIndex,
        currentProfile: username
      });
    }
  }, [location.state, username]);
  
  // PII Access states
  const [piiAccess, setPiiAccess] = useState({
    images: false,
    contact_info: false,
    contact_number: false,
    contact_email: false,
    date_of_birth: false,
    linkedin_url: false
  });
  const [piiRequestStatus, setPiiRequestStatus] = useState({});
  const [showPIIRequestModal, setShowPIIRequestModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null); // For PII request validation
  
  // Image Access states (new privacy system)
  const [accessibleImages, setAccessibleImages] = useState([]);
  const [showImageAccessModal, setShowImageAccessModal] = useState(false);
  const [selectedImageForAccess, setSelectedImageForAccess] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [hasMessages, setHasMessages] = useState(false);
  
  // Inline editing state (Phase 2: Full implementation)
  const [editingSection, setEditingSection] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  
  // Activation status state
  const [activationStatus, setActivationStatus] = useState(null);
  
  // Image lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [lightboxPan, setLightboxPan] = useState({ x: 0, y: 0 });
  const [lightboxDragging, setLightboxDragging] = useState(false);
  const lightboxDragStart = useRef({ x: 0, y: 0 });
  const lightboxPanStart = useRef({ x: 0, y: 0 });
  const lightboxIndexRef = useRef(0);
  const lightboxImagesRef = useRef([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);
  
  // Collapsed sections state (all collapsed by default)
  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: false,
    regionalCultural: true,
    personalLifestyle: true,
    educationHistory: true,
    workExperience: true,
    contactInformation: true,
    preferencesBackground: true,
    partnerCriteria: true
  });

  // Deep-link support (e.g. from email CTA): /profile/:username?open=contact
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get('open');
    if (open === 'contact') {
      setCollapsedSections(prev => ({
        ...prev,
        contactInformation: false
      }));
    }
  }, [location.search]);
  
  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };
  
  // Touch swipe state for mobile navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const currentUsername = localStorage.getItem('username');

  const formatPacificDateTime = (dateValue) => {
    if (!dateValue) return '—';
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    try {
      const formatted = date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Los_Angeles',
        timeZoneName: 'short'
      });
      return formatted.replace(',', '');
    } catch (e) {
      return formatFullDateTime(date);
    }
  };
  
  // Debug: Log user data when it changes
  useEffect(() => {
    if (user) {
      logger.debug('User data loaded:', {
        username: user.username,
        gender: user.gender,
        height: user.height,
        location: user.location,
        workingStatus: user.workingStatus
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Check if current user is admin
        const userRole = localStorage.getItem('userRole');
        const adminStatus = userRole === 'admin';
        setIsAdmin(adminStatus);
        
        // Pass requester to properly handle PII masking, and include context to avoid waterfall
        const res = await api.get(`/profile/${username}?requester=${currentUsername}&include_context=true`);
        logger.debug('API Response:', res);
        
        const profileData = res.data;
        setUser(profileData);

        if (!currentUsername || currentUsername === username) {
          setViewerViewMetrics({ lastViewedAt: null, viewCount: 0 });
        }
        
        // Populate consolidated context data
        if (profileData.kpiStats) {
          setKpiStats(profileData.kpiStats);
        }
        
        if (profileData.piiAccess) {
          setPiiAccess(profileData.piiAccess);
        }
        
        if (profileData.piiRequestStatus) {
          setPiiRequestStatus(profileData.piiRequestStatus);
        }
        
        if (profileData.isOnline !== undefined) {
          setIsOnline(profileData.isOnline);
        }

        // Load current user's contact numbers for SMS sharing
        if (currentUsername && !isOwnProfile) {
          loadCurrentUserContacts(currentUsername);
          loadProfileShares(currentUsername);
        }

        // Debug: Log relationship status from API
        logger.debug('💜 Profile relationship status from API:', {
          isFavorited: profileData.isFavorited,
          isShortlisted: profileData.isShortlisted,
          isExcluded: profileData.isExcluded,
          username: profileData.username
        });
        
        if (profileData.isFavorited !== undefined) {
          setIsFavorited(profileData.isFavorited);
        }
        
        if (profileData.isShortlisted !== undefined) {
          setIsShortlisted(profileData.isShortlisted);
        }
        
        if (profileData.isExcluded !== undefined) {
          setIsExcluded(profileData.isExcluded);
        }
        
        // Debug: Log visibility settings
        logger.debug('👁️ Profile data received - visibility settings:', {
          contactEmailVisible: profileData.contactEmailVisible,
          contactNumberVisible: profileData.contactNumberVisible,
          linkedinUrlVisible: profileData.linkedinUrlVisible,
          contactEmailMasked: profileData.contactEmailMasked,
          contactNumberMasked: profileData.contactNumberMasked,
          contactNumber: profileData.contactNumber ? 'present' : 'missing',
          contactEmail: profileData.contactEmail ? 'present' : 'missing'
        });
        
        // Check if this is the current user's profile
        setIsOwnProfile(currentUsername === username);
        
        // Main profile payload is ready; stop blocking UI on secondary calls.
        setLoading(false);

        // Track profile view (only if viewing someone else's profile)
        if (currentUsername && currentUsername !== username) {
          // Guard against React StrictMode double-invocation (and rapid re-renders):
          // only fetch metrics + record a view ONCE per profile per component instance.
          const trackKey = `${currentUsername}->${username}`;
          if (!trackedViewsRef.current.has(trackKey)) {
            trackedViewsRef.current.add(trackKey);

            // Fire non-critical tracking calls in background.
            (async () => {
              try {
                const metricsRes = await api.get(`/profile-views/${username}/viewer-metrics`);
                const data = metricsRes?.data || {};
                setViewerViewMetrics({
                  lastViewedAt: data.lastViewedAt || null,
                  viewCount: Number.isFinite(data.viewCount) ? data.viewCount : 0
                });
              } catch (metricsErr) {
                logger.debug('Error loading viewer view metrics:', metricsErr);
              }
              try {
                await api.post('/profile-views', {
                  profileUsername: username,
                  viewedByUsername: currentUsername
                });
              } catch (viewErr) {
                logger.debug('Error tracking profile view:', viewErr);
              }
            })();
          }
          
          // Fetch current user's profile for PII request validation
          // (Still needed if not already cached, but could be optimized later)
          if (!currentUserProfile) {
            api.get(`/profile/${currentUsername}?requester=${currentUsername}`)
              .then((myProfileRes) => setCurrentUserProfile(myProfileRes.data))
              .catch((profileErr) => logger.debug("Error fetching current user profile:", profileErr));
          }

          // Use backend-provided context data instead of redundant API calls
          // Backend now returns: isFavorited, isShortlisted, kpiStats, piiRequestStatus, piiAccess, isOnline
          if (profileData.isFavorited !== undefined) {
            setIsFavorited(profileData.isFavorited);
          }
          if (profileData.isShortlisted !== undefined) {
            setIsShortlisted(profileData.isShortlisted);
          }
          if (profileData.kpiStats) {
            setKpiStats(profileData.kpiStats);
          }
          if (profileData.piiRequestStatus) {
            setPiiRequestStatus(profileData.piiRequestStatus);
          }
          if (profileData.piiAccess) {
            setPiiAccess(profileData.piiAccess);
          }
          if (profileData.isOnline !== undefined) {
            setIsOnline(profileData.isOnline);
          }

          // Load accessible images with privacy settings (Legacy system check) in background
          loadAccessibleImages();

          // Check if there are existing messages with this user
          api.get(`/messages/conversation/${username}?username=${currentUsername}`)
            .then((messagesResponse) => {
              const messages = messagesResponse.data.messages || messagesResponse.data || [];
              logger.debug('💬 Messages check:', { username, currentUsername, hasMessages: messages.length > 0, count: messages.length });
              setHasMessages(messages.length > 0);
            })
            .catch((msgErr) => logger.debug("Error checking messages:", msgErr));
        }
        
        // Activation status if viewing own profile
        if (currentUsername === username) {
          fetchActivationStatus();
        }
      } catch (err) {
        logger.error("Error fetching profile:", err);
        logger.error("Error details:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          username: username,
          currentUsername: currentUsername
        });
        
        // More specific error messages
        if (err.response?.status === 404) {
          setError(getApiErrorMessage(err, `Profile not found: ${username}`));
        } else if (err.response?.status === 403) {
          setError(getApiErrorMessage(err, "You don't have permission to view this profile"));
        } else if (err.response?.data?.detail) {
          setError(getApiErrorMessage(err));
        } else {
          setError(`Unable to load profile: ${err.message || 'Unknown error'}`);
        }
      } finally {
        // Keep this as a safety fallback when primary request fails before setLoading(false).
        setLoading(false);
      }
    };
    fetchProfile();
    
    // Poll for PII access changes every 30 seconds (reduced from 10s)
    const accessCheckInterval = setInterval(() => {
      if (currentUsername && currentUsername !== username) {
        checkPIIAccess();
      }
    }, 30000);

    // Poll for KPI stats updates every 60 seconds (reduced from 15s)
    const kpiStatsInterval = setInterval(() => {
      fetchKPIStats();
    }, 60000);
    
    // Refresh KPI stats when page regains focus
    const handleFocus = () => {
      logger.debug('Page focused - refreshing KPI stats');
      fetchKPIStats();
    };
    window.addEventListener('focus', handleFocus);
    
    // Subscribe to online status changes for this user
    const unsubscribe = onlineStatusService.subscribe((changedUsername, online) => {
      if (changedUsername === username) {
        logger.debug(`Status update for ${username}:`, online ? 'online' : 'offline');
        setIsOnline(online);
      }
    });
    
    return () => {
      clearInterval(accessCheckInterval);
      clearInterval(kpiStatsInterval);
      window.removeEventListener('focus', handleFocus);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, currentUsername]);

  // Fetch L3V3L score: check cache first, fallback to quick score
  const applyL3v3lData = (data) => {
    setL3v3lIsQuickScore(data.isQuickScore || false);
    if (data.isQuickScore) {
      setL3v3lMatchData({ overall: data.score, compatibilityLevel: data.level, matchReasons: [] });
    } else {
      setL3v3lMatchData({
        overall: data.score, compatibilityLevel: data.level,
        gender: data.breakdown?.gender || 0, l3v3l_pillars: data.breakdown?.l3v3l_pillars || 0,
        demographics: data.breakdown?.demographics || 0, partner_preferences: data.breakdown?.partner_preferences || 0,
        habits_personality: data.breakdown?.habits_personality || 0, career_education: data.breakdown?.career_education || 0,
        physical_attributes: data.breakdown?.physical_attributes || 0, cultural_factors: data.breakdown?.cultural_factors || 0,
        matchReasons: data.matchReasons || []
      });
    }
  };

  const handleRefreshL3v3l = async () => {
    setL3v3lRefreshing(true);
    try {
      const response = await api.post(`/l3v3l-score/${currentUsername}/${username}/refresh`);
      if (response.data) applyL3v3lData(response.data);
    } catch (error) {
      logger.error('L3V3L refresh failed:', error);
    } finally {
      setL3v3lRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchL3V3LMatchData = async () => {
      if (!currentUsername || isOwnProfile) return;
      try {
        const response = await api.get(`/l3v3l-score/${currentUsername}/${username}`);
        if (response.data) applyL3v3lData(response.data);
      } catch (error) {
        logger.debug('No L3V3L data available');
      }
    };
    fetchL3V3LMatchData();
  }, [username, currentUsername, isOwnProfile]);

  const fetchActivationStatus = async () => {
    try {
      const response = await verificationApi.get(`/api/verification/status/${username}`);
      setActivationStatus(response.data);
      logger.debug('Activation status loaded:', response.data);
    } catch (error) {
      logger.error('Error fetching activation status:', error);
      // Don't fail silently, but don't block profile loading
    }
  };

  const handleResendEmail = async () => {
    try {
      const response = await verificationApi.post('/api/verification/resend-verification', {
        username: username
      });
      if (response.data.success) {
        setSuccessMessage('✅ Verification email sent! Please check your inbox.');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setStatusMessage(response.data.message || '❌ Failed to resend email.');
        setTimeout(() => setStatusMessage(''), 5000);
      }
    } catch (error) {
      logger.error('Resend email error:', error);
      setStatusMessage(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        '❌ Failed to resend verification email.'
      );
      setTimeout(() => setStatusMessage(''), 5000);
    }
  };

  const checkPIIAccess = async () => {
    if (!currentUsername || isOwnProfile) return;

    // ✅ ADMIN BYPASS - Admins have full access to all PII
    if (isAdmin) {
      logger.debug('Admin user - full PII access');
      setPiiAccess({
        images: true,
        contact_info: true,
        contact_number: true,
        contact_email: true,
        date_of_birth: true,
        linkedin_url: true
      });
      return;
    }

    try {
      // Use backend-provided piiAccess and piiRequestStatus from profile response
      // Only fetch fresh data if not already available
      if (user && user.piiAccess) {
        setPiiAccess(user.piiAccess);
      } else {
        // Fallback to API calls if backend doesn't provide piiAccess
        const [imagesRes, contactNumberRes, contactEmailRes, dobRes, linkedinRes] = await Promise.all([
          api.get(`/pii-access/check?requester=${currentUsername}&profile_owner=${username}&access_type=images`),
          api.get(`/pii-access/check?requester=${currentUsername}&profile_owner=${username}&access_type=contact_number`),
          api.get(`/pii-access/check?requester=${currentUsername}&profile_owner=${username}&access_type=contact_email`),
          api.get(`/pii-access/check?requester=${currentUsername}&profile_owner=${username}&access_type=date_of_birth`),
          api.get(`/pii-access/check?requester=${currentUsername}&profile_owner=${username}&access_type=linkedin_url`)
        ]);

        setPiiAccess({
          images: imagesRes.data.hasAccess,
          contact_info: contactNumberRes.data.hasAccess || contactEmailRes.data.hasAccess,
          contact_number: contactNumberRes.data.hasAccess,
          contact_email: contactEmailRes.data.hasAccess,
          date_of_birth: dobRes.data.hasAccess,
          linkedin_url: linkedinRes.data.hasAccess
        });
      }

      // Use backend-provided piiRequestStatus if available
      if (user.piiRequestStatus) {
        setPiiRequestStatus(user.piiRequestStatus);
      } else {
        // Fallback to fetching request status
        const requestStatus = {};

        try {
          const outgoingRes = await api.get(`/pii-requests/${currentUsername}/outgoing`);
          const outgoingRequests = outgoingRes.data.requests || [];

          outgoingRequests.forEach(req => {
            if (req.profileUsername === username && req.status === 'pending') {
              const typeMap = {
                'images': 'images',
                'contact_number': 'contact_number',
                'contact_email': 'contact_email',
                'date_of_birth': 'date_of_birth',
                'linkedin_url': 'linkedin_url'
              };
              const statusKey = typeMap[req.requestType];
              if (statusKey) {
                requestStatus[statusKey] = 'pending';
              }
            }
          });
        } catch (err) {
          logger.error('Error fetching outgoing requests:', err);
        }

        setPiiRequestStatus(requestStatus);
      }
    } catch (err) {
      logger.error("Error checking PII access:", err);
    }
  };

  // ===== SHARED ACTION HANDLERS (used by both sticky sidebar & bottom bar) =====
  const handleToggleFavorite = async () => {
    try {
      const currentUser = localStorage.getItem('username');
      if (isFavorited) {
        await api.delete(`/favorites/${username}?username=${encodeURIComponent(currentUser)}`);
        setIsFavorited(false);
        setSuccessMessage('✅ Removed from favorites');
      } else {
        const res = await api.post(`/favorites/${username}?username=${encodeURIComponent(currentUser)}`);
      if (res?.data?.mutual) toastService.success('💞 Mutual favorites — they favorited you back!');
        setIsFavorited(true);
        setSuccessMessage('✅ Added to favorites');
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      logger.error('❌ Favorites error:', err.response?.data || err.message);
      if (err.response?.status === 409) {
        setIsFavorited(true);
        setSuccessMessage('Already in favorites');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorMsg = err.response?.data?.detail || 'Failed to update favorites';
        showActionError(errorMsg);
      }
    }
  };

  const handleToggleShortlist = async () => {
    try {
      const currentUser = localStorage.getItem('username');
      if (isShortlisted) {
        await api.delete(`/shortlist/${username}?username=${encodeURIComponent(currentUser)}`);
        setIsShortlisted(false);
        setSuccessMessage('✅ Removed from shortlist');
      } else {
        const res = await api.post(`/shortlist/${username}?username=${encodeURIComponent(currentUser)}`);
      if (res?.data?.mutual) toastService.success('💞 Mutual interest — they shortlisted you back!');
        setIsShortlisted(true);
        setSuccessMessage('✅ Added to shortlist');
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      showActionError('Failed to update shortlist');
    }
  };

  const handleOpenPIIRequest = async () => {
    logger.debug('Opening PII Request Modal');
    await checkPIIAccess();
    setShowPIIRequestModal(true);
  };

  const handleToggleExclude = async () => {
    try {
      if (isExcluded) {
        await api.delete(`/exclusions/${username}`);
        setIsExcluded(false);
        setSuccessMessage('✅ Removed from not interested');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setExclusionLoading(true);
        const response = await api.get(`/exclusions/preview/${username}`);
        setExclusionPreviewData(response.data);
        setShowExclusionPreview(true);
        setExclusionLoading(false);
      }
    } catch (err) {
      setExclusionLoading(false);
      showActionError('Failed to update not interested');
    }
  };

  // Load current user's contact numbers for SMS sharing
  const loadCurrentUserContacts = async (currentUser) => {
    try {
      logger.debug('Loading contacts for current user:', { currentUser, viewingProfile: username });
      const res = await api.get(`/profile/${currentUser}`);
      const contacts = res.data.contactNumbers || [];
      setCurrentUserContacts(contacts);
      logger.debug('Loaded user contacts:', { currentUser, contacts, count: contacts.length });
    } catch (err) {
      logger.error('Failed to load user contacts:', err);
    }
  };

  // Load profile share history for the currently viewed profile
  const loadProfileShares = async (currentUser) => {
    try {
      const res = await api.get('/profile-shares', {
        params: {
          shared_profile_username: username
        }
      });
      setProfileShares(res.data.shares || []);
    } catch (err) {
      logger.error('Failed to load profile shares:', err);
    }
  };

  // Handle recipient selection change
  const handleRecipientChange = (recipientType) => {
    setShareRecipient(recipientType);
    if (!recipientType) {
      setSharePhone('');
      return;
    }
    const contact = currentUserContacts.find(c => c.label && c.label.toLowerCase() === recipientType.toLowerCase());
    const phone = contact?.number || '';
    setSharePhone(phone);
    logger.debug('Recipient changed:', { recipientType, contact, phone, allContacts: currentUserContacts, contactFound: !!contact });
  };

  // Generate default message
  const generateDefaultMessage = () => {
    const profileUrl = `${window.location.origin}/profile/${username}`;
    return `Hi! I found this profile on L3V3L Matches that might be a good match for your search.

${user.firstName}, ${user.age} - ${user.location} - ${user.height}
${user.profession}

View profile: [url=${profileUrl}]

Sent from L3V3L Matches`;
  };

  // Open message editor with default message
  const handleEditMessage = () => {
    setCustomMessage(generateDefaultMessage());
    setShowMessageEditor(true);
  };

  // Send profile share via SMS
  const handleSendSMS = async (messageOverride = null, recipientTypeOverride = null, phoneOverride = null) => {
    const finalRecipient = recipientTypeOverride || shareRecipient;
    const finalPhone = phoneOverride || sharePhone;
    
    if (!finalRecipient || !finalPhone) {
      showActionError('Please select a recipient and enter a phone number');
      return;
    }

    setShareSending(true);
    setActionError(''); // Clear any previous errors
    try {
      const currentUser = localStorage.getItem('username');
      const message = messageOverride || customMessage || generateDefaultMessage();

      await api.post('/send-sms', {
        recipientPhone: finalPhone,
        message: message,
        username: currentUser,
        recipientType: finalRecipient,
        sharedProfileUsername: username
      });

      setSuccessMessage('✅ Profile shared via SMS');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowMessageEditor(false);
      setCustomMessage('');

      // Reload share history
      loadProfileShares(currentUser);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to send SMS';
      logger.error('SMS send error:', errorMsg);
      showActionError(errorMsg);
    } finally {
      setShareSending(false);
    }
  };

  // Reshare profile from history
  const handleReshare = async (share) => {
    setShareRecipient(share.recipientType);
    setSharePhone(share.recipientPhone);
    setShareSending(true);

    try {
      const currentUser = localStorage.getItem('username');
      const profileUrl = `${window.location.origin}/profile/${share.sharedProfileUsername}`;

      const message = `Hi! I found this profile on L3V3L Matches that might be a good match for your search.

View profile: [url=${profileUrl}]

Sent from L3V3L Matches`;

      await api.post('/send-sms', {
        recipientPhone: share.recipientPhone,
        message: message,
        username: currentUser,
        recipientType: share.recipientType,
        sharedProfileUsername: share.sharedProfileUsername
      });

      setSuccessMessage('✅ Profile reshared via SMS');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Reload share history
      loadProfileShares(currentUser);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to reshare';
      logger.error('Reshare error:', errorMsg);
      showActionError(errorMsg);
    } finally {
      setShareSending(false);
    }
  };

  // Delete a profile share record
  const handleDeleteShare = async (share) => {
    try {
      const currentUser = localStorage.getItem('username');
      await api.delete(`/profile-shares/${share.sharedProfileUsername}`, {
        params: {
          recipient_type: share.recipientType,
          recipient_phone: share.recipientPhone
        }
      });

      setSuccessMessage('✅ Share record deleted');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Reload share history
      loadProfileShares(currentUser);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete';
      logger.error('Delete share error:', errorMsg);
      showActionError(errorMsg);
    }
  };

  // Load accessible images with privacy settings and expiry info
  const loadAccessibleImages = async () => {
    if (isOwnProfile || !currentUsername || !user) {
      // Owner sees all images normally, or user not loaded yet
      return;
    }

    try {
      // Use the new per-image access check endpoint
      // This accounts for one-time views, expiry, and per-image access rules
      const accessResponse = await api.get(`/pii-access/check-images`, {
        params: {
          requester: currentUsername,
          profile_owner: username
        }
      });
      
      const imageAccessList = accessResponse.data.images || [];
      
      if (user?.images?.length > 0) {
        // Create image objects with per-image access status
        const imagesWithAccess = user.images.map((img, idx) => {
          const accessInfo = imageAccessList.find(a => a.index === idx) || { hasAccess: false, reason: 'unknown' };
          return {
            imageId: `${username}-img-${idx}`,
            imageUrl: img,
            imageOrder: idx,
            isProfilePic: idx === 0,
            hasAccess: accessInfo.hasAccess,
            accessReason: accessInfo.reason,
            // For images without access, show blurred
            initialVisibility: accessInfo.hasAccess 
              ? { type: 'clear' } 
              : { type: 'blurred', blurLevel: 'heavy' }
          };
        });
        
        logger.debug('Loaded images with per-image access:', imagesWithAccess);
        setAccessibleImages(imagesWithAccess);
      } else {
        logger.debug('No images to load');
        setAccessibleImages([]);
      }
    } catch (err) {
      logger.error("Error loading accessible images:", err);
      setAccessibleImages([]);
    }
  };
  
  // Listen for PII access changes (when owner grants/revokes access)
  useEffect(() => {
    const cleanup = onPIIAccessChange(async (detail) => {
      const { action, targetUsername, ownerUsername } = detail;
      
      logger.debug('PII Access Change Event:', detail);
      
      // If this is about access to the current profile we're viewing
      if (ownerUsername === username && targetUsername === currentUsername) {
        logger.debug('Refreshing access');
        
        // Reload PII access status
        await checkPIIAccess();
        
        // Reload accessible images
        await loadAccessibleImages();
        
        // Show success message
        if (action === 'granted') {
          setSuccessMessage('🎉 Access granted! Images are now visible.');
          setTimeout(() => setSuccessMessage(''), 5000);
        } else if (action === 'revoked') {
          setSuccessMessage('Access has been revoked.');
          setTimeout(() => setSuccessMessage(''), 5000);
        }
      }
    });
    
    // Cleanup listener on unmount
    return cleanup;
  }, [username, currentUsername]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load accessible images when PII access is granted
  useEffect(() => {
    if (piiAccess.images && !isOwnProfile && user && user.images?.length > 0) {
      logger.debug('Auto-loading accessible images');
      loadAccessibleImages();
    }
  }, [piiAccess.images, user, isOwnProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle access request
  // eslint-disable-next-line no-unused-vars
  const handleRequestAccess = (image) => {
    setSelectedImageForAccess(image);
    setShowImageAccessModal(true);
  };

  // Handle renewal request
  // eslint-disable-next-line no-unused-vars
  const handleRenewAccess = (image) => {
    setSelectedImageForAccess(image);
    setShowImageAccessModal(true);
  };

  // Submit access request
  const handleSubmitAccessRequest = async (requestData) => {
    try {
      // Validate we have necessary data
      if (!currentUsername) {
        showActionError('You must be logged in to request access');
        return;
      }

      if (!user.images || user.images.length === 0) {
        showActionError('No images to request access for');
        return;
      }

      logger.debug('Submitting access request:', {
        requesterUsername: currentUsername,
        ownerUsername: username,
        imageCount: user.images.length
      });

      const response = await imageAccess.requestAccess({
        requesterUsername: currentUsername,
        ownerUsername: username,
        imageIds: user.images.map((img, idx) => `${username}-img-${idx}`),
        message: requestData.message || ''
      });

      logger.debug('Access request response:', response);

      setSuccessMessage('✅ Access request sent successfully!');
      setShowImageAccessModal(false);
      setSelectedImageForAccess(null);
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh accessible images
      await loadAccessibleImages();
    } catch (err) {
      logger.error('Error requesting access:', err);
      logger.error('Error details:', err.response?.data);
      showActionError(err.response?.data?.message || 'Failed to send access request');
    }
  };

  const fetchKPIStats = async () => {
    try {
      logger.debug('Fetching KPI stats for:', username);
      
      const [viewsRes, shortlistRes, favoritesRes] = await Promise.all([
        api.get(`/profile-views/${username}/count`),
        api.get(`/their-shortlists/${username}`),
        api.get(`/their-favorites/${username}`)
      ]);
      
      logger.debug('Views Response:', viewsRes.data);
      logger.debug('Shortlist Response:', shortlistRes.data);
      logger.debug('Favorites Response:', favoritesRes.data);
      
      const stats = {
        profileViews: viewsRes.data?.totalViews || viewsRes.data?.uniqueViewers || 0,
        shortlistedBy: shortlistRes.data?.users?.length || 0,
        favoritedBy: favoritesRes.data?.users?.length || 0
      };
      
      logger.debug('Parsed KPI stats:', stats);
      setKpiStats(stats);
    } catch (err) {
      logger.error("❌ Error fetching KPI stats:", err);
    }
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 5000);
  };

  const resolveShareUrl = async () => {
    if (!user?.profileId) {
      return '';
    }

    const fallbackUrl = getProfileShareFallbackUrl(user.profileId, window.location.origin);

    try {
      const res = await api.get(`/share-profile/${user.profileId}`);
      return res.data?.tinyUrl || fallbackUrl;
    } catch (err) {
      logger.warn('⚠️ Falling back to local profile share URL:', err);
      return fallbackUrl;
    }
  };

  const openLightbox = (imageUrl, imageSet) => {
    const images = imageSet && imageSet.length > 0 ? imageSet : [imageUrl];
    const idx = images.indexOf(imageUrl);
    const safeIdx = idx >= 0 ? idx : 0;
    lightboxImagesRef.current = images;
    lightboxIndexRef.current = safeIdx;
    setLightboxImages(images);
    setLightboxIndex(safeIdx);
    setLightboxImage(imageUrl);
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setLightboxImage(null);
    setLightboxImages([]);
    setLightboxIndex(0);
    lightboxIndexRef.current = 0;
    lightboxImagesRef.current = [];
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
    setLightboxDragging(false);
  };

  const lightboxNavigate = (dir) => {
    const imgs = lightboxImagesRef.current;
    if (!imgs || imgs.length <= 1) return;
    const next = (lightboxIndexRef.current + dir + imgs.length) % imgs.length;
    lightboxIndexRef.current = next;
    setLightboxIndex(next);
    setLightboxImage(imgs[next]);
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
  };

  const lightboxZoomIn = (e) => {
    e && e.stopPropagation();
    setLightboxZoom(z => Math.min(z + 0.5, 5));
  };

  const lightboxZoomOut = (e) => {
    e && e.stopPropagation();
    setLightboxZoom(z => {
      const next = Math.max(z - 0.5, 1);
      if (next === 1) setLightboxPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleShareButtonClick = (e) => {
    e.stopPropagation();
    if (!user?.profileId) {
      showToast('Profile link is unavailable', 'warning');
      return;
    }

    setShowShareMenu((prev) => !prev);
  };

  const handleShareOption = async (option) => {
    if (!user?.profileId) {
      showToast('Profile link is unavailable', 'warning');
      return;
    }

    setShowShareMenu(false);

    try {
      const shareUrl = await resolveShareUrl();
      const shareMessage = buildProfileShareMessage(
        {
          name: user.name || user.fullName || user.displayName,
          username: user.username,
          profileId: user.profileId
        },
        shareUrl
      );

      if (option === 'copy') {
        await copyTextToClipboard(shareUrl);
        showToast('🔗 Profile link copied!', 'success');
        return;
      }

      if (option === 'whatsapp') {
        window.open(buildWhatsAppShareUrl(shareMessage), '_blank', 'noopener,noreferrer');
        showToast('💬 WhatsApp share opened', 'success');
        return;
      }

      if (option === 'sms') {
        window.location.href = buildSmsShareUrl(shareMessage);
        showToast('📩 SMS share opened', 'success');
      }
    } catch (err) {
      logger.error('❌ Failed to share profile:', err);
      showToast('Unable to share profile right now', 'error');
    }
  };

  // Carousel navigation handlers
  const handlePreviousProfile = () => {
    if (searchResults && currentIndex !== null && currentIndex > 0) {
      const prevUser = searchResults[currentIndex - 1];
      navigate(`/profile/${prevUser.username}`, {
        state: {
          searchResults,
          currentIndex: currentIndex - 1,
          fromSearch: true
        }
      });
    }
  };

  const handleNextProfile = () => {
    if (searchResults && currentIndex !== null && currentIndex < searchResults.length - 1) {
      const nextUser = searchResults[currentIndex + 1];
      navigate(`/profile/${nextUser.username}`, {
        state: {
          searchResults,
          currentIndex: currentIndex + 1,
          fromSearch: true
        }
      });
    }
  };

  const handleBackToSearch = () => {
    navigate('/search'); // Navigate directly back to search page
  };

  // Touch swipe gesture handlers
  const minSwipeDistance = 50; // Minimum distance for a swipe (in pixels)

  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset touch end
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (searchResults && currentIndex !== null) {
      if (isLeftSwipe) {
        // Swipe left = Next profile
        logger.debug('Swipe left - next profile');
        handleNextProfile();
      } else if (isRightSwipe) {
        // Swipe right = Previous profile
        logger.debug('Swipe right - previous profile');
        handlePreviousProfile();
      }
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditFormData({});
  };

  // Handle form input changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save inline edits
  const handleSaveEdit = async (section) => {
    setSavingSection(section);
    
    try {
      // Prepare FormData for API
      const formData = new FormData();
      
      logger.debug('Saving section:', section);
      logger.debug('Edit form data:', editFormData);
      
      // Add fields based on section - send all fields from editFormData
      if (section === 'basic') {
        // Send all fields with their current values from editFormData
        const fieldsToSave = [
          'gender', 'height', 'location', 'religion', 'relationshipStatus',
          'lookingFor', 'workingStatus', 
          'citizenshipStatus'
        ];
        
        fieldsToSave.forEach(field => {
          const value = editFormData[field];
          // Only send fields that have actual values (not empty strings)
          if (value !== undefined && value !== null && value !== '') {
            formData.append(field, value);
            logger.debug(`Saving field: ${field}`);
          } else {
            // Skipped empty field
          }
        });
      } else if (section === 'contact') {
        if (editFormData.contactNumber !== undefined && editFormData.contactNumber !== null && editFormData.contactNumber !== '') {
          formData.append('contactNumber', editFormData.contactNumber);
          logger.debug('Saving contactNumber');
        }
        if (editFormData.contactEmail !== undefined && editFormData.contactEmail !== null && editFormData.contactEmail !== '') {
          formData.append('contactEmail', editFormData.contactEmail);
          logger.debug('Saving contactEmail');
        }
      } else if (section === 'dateOfBirth') {
        if (editFormData.dateOfBirth !== undefined && editFormData.dateOfBirth !== null && editFormData.dateOfBirth !== '') {
          formData.append('dateOfBirth', editFormData.dateOfBirth);
          logger.debug('Saving dateOfBirth');
        }
      }
      
      // Check if we have any fields to update
      let hasFields = false;
      // eslint-disable-next-line no-unused-vars
      for (const _pair of formData.entries()) {
        hasFields = true;
        break;
      }
      
      if (!hasFields) {
        logger.warn('⚠️ No fields to update - canceling save');
        showToast('No changes to save', 'info');
        setEditingSection(null);
        setEditFormData({});
        setSavingSection(null);
        return;
      }
      
      // Make API call
      const response = await api.put(`/profile/${username}`, formData);
      
      logger.debug('API Response:', response.data);
      
      // Update user data with the response (API returns {message, user})
      setUser(response.data.user || response.data);
      
      // Close editing and show success
      setEditingSection(null);
      setEditFormData({});
      showToast('Profile updated successfully!', 'success');
      
    } catch (err) {
      logger.error('❌ Error saving profile:', err);
      logger.error('Error details:', err.response?.data);
      showToast('Failed to update profile. Please try again.', 'error');
    } finally {
      setSavingSection(null);
    }
  };

  // Handle About Me edit
  const handleEditAboutMe = () => {
    // Initialize with custom content or strip HTML from generated content
    const currentContent = user.customAboutMe || 
      generateAboutMe(user).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    setEditedAboutMe(currentContent);
    setIsEditingAboutMe(true);
  };

  const handleSaveAboutMe = async () => {
    setSavingAboutMe(true);
    try {
      const formData = new FormData();
      formData.append('customAboutMe', editedAboutMe);
      
      const response = await api.put(`/profile/${username}`, formData);
      setUser(response.data.user || response.data);
      setIsEditingAboutMe(false);
      showToast('About Me updated successfully!', 'success');
    } catch (err) {
      logger.error('Error saving About Me:', err);
      showToast('Failed to update About Me', 'error');
    } finally {
      setSavingAboutMe(false);
    }
  };

  const handleResetAboutMe = async () => {
    setSavingAboutMe(true);
    try {
      const formData = new FormData();
      formData.append('customAboutMe', '__RESET__');  // Special value to clear custom content
      
      const response = await api.put(`/profile/${username}`, formData);
      setUser(response.data.user || response.data);
      setIsEditingAboutMe(false);
      showToast('About Me reset to auto-generated content', 'success');
    } catch (err) {
      logger.error('Error resetting About Me:', err);
      showToast('Failed to reset About Me', 'error');
    } finally {
      setSavingAboutMe(false);
    }
  };

  // Handle Partner Preference ("What You're Looking For") edit
  const handleEditPartnerPref = () => {
    // Initialize with custom content or strip HTML from generated content
    const currentContent = user.customPartnerPreference || 
      generatePartnerPreference(user).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    setEditedPartnerPref(currentContent);
    setIsEditingPartnerPref(true);
  };

  const handleSavePartnerPref = async () => {
    setSavingPartnerPref(true);
    try {
      const formData = new FormData();
      formData.append('customPartnerPreference', editedPartnerPref);
      
      const response = await api.put(`/profile/${username}`, formData);
      setUser(response.data.user || response.data);
      setIsEditingPartnerPref(false);
      showToast('Partner preferences updated successfully!', 'success');
    } catch (err) {
      logger.error('Error saving Partner Preference:', err);
      showToast('Failed to update partner preferences', 'error');
    } finally {
      setSavingPartnerPref(false);
    }
  };

  const handleResetPartnerPref = async () => {
    setSavingPartnerPref(true);
    try {
      const formData = new FormData();
      formData.append('customPartnerPreference', '__RESET__');  // Special value to clear custom content
      
      const response = await api.put(`/profile/${username}`, formData);
      setUser(response.data.user || response.data);
      setIsEditingPartnerPref(false);
      showToast('Partner preferences reset to auto-generated content', 'success');
    } catch (err) {
      logger.error('Error resetting Partner Preference:', err);
      showToast('Failed to reset partner preferences', 'error');
    } finally {
      setSavingPartnerPref(false);
    }
  };

  // AI Rephrase handler
  const handleRephraseWithAI = async () => {
    if (!editedAboutMe || editedAboutMe.trim().length < 20) {
      showToast('Please enter at least 20 characters to rephrase', 'warning');
      return;
    }
    
    setIsRephrasing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getBackendUrl()}/api/ai/rephrase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: editedAboutMe.replace(/<[^>]*>/g, ''), // Strip HTML tags
          style: rephraseStyle,
          provider: aiProvider
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.rephrased_text) {
        setEditedAboutMe(data.rephrased_text);
        showToast('✨ Text rephrased with AI!', 'success');
      } else {
        showToast(data.error || 'Failed to rephrase text', 'error');
      }
    } catch (err) {
      logger.error('Error rephrasing with AI:', err);
      showToast('Failed to connect to AI service', 'error');
    } finally {
      setIsRephrasing(false);
    }
  };

  if (loading) return <p>Loading profile...</p>;
  if (error) {
    if (isMembershipRequiredError(error)) {
      return (
        <div className="pf-narrow-center">
          <div className="search-membership-gate">
            <div className="search-membership-gate-lock" aria-hidden="true">🔒</div>
            <div className="search-membership-gate-title">Member profiles are a member feature</div>
            <div className="search-membership-gate-sub">
              {isNativePlatform()
                ? 'Your account is not activated yet.'
                : 'Activate membership to view other profiles.'}
            </div>
            {!isNativePlatform() && (
              <div className="search-membership-gate-actions">
                <button
                  type="button"
                  className="search-gate-btn search-gate-btn-primary"
                  onClick={() => window.dispatchEvent(new CustomEvent('force-contribution-popup'))}
                >
                  Activate membership
                </button>
                <button
                  type="button"
                  className="search-gate-btn search-gate-btn-link"
                  onClick={() => navigate('/messages')}
                >
                  View messages
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }
    return <p className="text-danger">{error}</p>;
  }
  if (!user) return <p>No profile found.</p>;

  const age = (() => {
    if (!user.birthMonth || !user.birthYear) return null;
    const today = new Date();
    let a = today.getFullYear() - user.birthYear;
    if (today.getMonth() + 1 < user.birthMonth) a--;
    return a;
  })();

  const publicImages = Array.isArray(user.publicImages) ? user.publicImages : [];
  const publicImageObjects = publicImages.map((img, idx) => ({
    imageId: `${username}-public-${idx}`,
    imageUrl: img,
    imageOrder: idx,
    isProfilePic: false,
    hasAccess: false,
    initialVisibility: { type: 'clear' }
  }));
  // publicAvatar available for future use if needed
  // const publicAvatar = publicImages?.[0] || null;
  
  // Check if user has all access
  const hasAllAccess = isOwnProfile || (piiAccess.images && piiAccess.contact_info && piiAccess.date_of_birth);

  // Shared header actions — rendered in the identity row for member view,
  // in the right-hand action group for own profile
  const shareMenuJSX = user.profileId ? (
    <div className="share-profile-wrapper" ref={shareMenuRef}>
      <button
        className="share-profile-btn"
        onClick={handleShareButtonClick}
        aria-haspopup="menu"
        aria-expanded={showShareMenu}
        title="Share profile"
        type="button"
      >
        <span className="share-icon" aria-hidden="true">🔗</span>
        <span className="share-label">Share</span>
        <span className="share-caret" aria-hidden="true">▾</span>
      </button>

      {showShareMenu && (
        <div
          className="share-profile-menu"
          role="menu"
          aria-label="Share profile options"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="share-profile-option share-profile-option-whatsapp"
            onClick={() => handleShareOption('whatsapp')}
            disabled={false}
            role="menuitem"
          >
            💬 WhatsApp
          </button>
          <button
            type="button"
            className="share-profile-option share-profile-option-sms"
            onClick={() => handleShareOption('sms')}
            disabled={false}
            role="menuitem"
          >
            📩 SMS Text
          </button>
          <button
            type="button"
            className="share-profile-option share-profile-option-copy"
            onClick={() => handleShareOption('copy')}
            disabled={false}
            role="menuitem"
          >
            🔗 Copy Link
          </button>
        </div>
      )}
    </div>
  ) : null;

  const activityButtonJSX = isAdmin ? (
    <button
      className="activity-summary-btn"
      onClick={() => setShowActivityPanel(true)}
      title="View user activity summary (Admin)"
    >
      <span className="activity-icon" aria-hidden="true">📊</span>
      <span className="activity-label">Activity</span>
    </button>
  ) : null;

  const lastActiveAt = user.status?.last_seen || user.lastActive || user.lastActiveAt || user.lastLogin;

  // Verification Trust Badges — rendered inside the action group
  const trustRowJSX = (
    <span className="profile-trust-row">
      <span
        className={`trust-badge ${user.profileCreatedBy ? 'trust-active' : 'trust-inactive'}`}
        title={user.profileCreatedBy ? `Profile created by ${user.profileCreatedBy === 'me' ? 'self' : user.profileCreatedBy}` : 'Self or Parent — not specified'}
      >
        {user.profileCreatedBy === 'parent' ? '👨‍👩‍👧' : user.profileCreatedBy === 'other' ? '\u{1F91D}' : '👤'}
      </span>
      <span
        className={`trust-badge ${user.badges?.communityVerified ? 'trust-active' : 'trust-inactive'}`}
        title={user.badges?.communityVerified ? 'Community Verified' : 'Community Verified — pending'}
      >
        🤝
      </span>
      <span
        className={`trust-badge ${user.badges?.idVerified ? 'trust-active' : 'trust-inactive'}`}
        title={user.badges?.idVerified ? 'ID.me Verified' : 'ID.me Verified — pending'}
      >
        🛡️
      </span>
      <span
        className={`trust-badge ${user.membership?.status === 'active' ? 'trust-active' : 'trust-inactive'}`}
        title={user.membership?.status === 'active' ? 'Contributing Member' : 'Not a contributing member yet'}
      >
        🪪
      </span>
    </span>
  );

  return (
    <div 
      className={`profile-page-wide ${embedded ? 'profile-embedded' : 'mt-4'}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Toast Notification */}
      {toast.show && (
        <div className={`pf-mini-toast pf-toast-${toast.type}`}>
          <span className="pf-icon-lg">
            {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
          </span>
          <span className="pf-toast-text">{toast.message}</span>
          <button 
            onClick={() => setToast({ show: false, message: '', type: '' })}
            className="pf-toast-close"
          >×</button>
        </div>
      )}
      
      {/* Status Message Bubble */}
      {statusMessage && (
        <div className="status-bubble pf-float-toast pf-toast-warn" >
          <span className="pf-icon-lg">⚠️</span>
          <div className="pf-flex-fill">
            <strong className="pf-toast-title">
              Account Status
            </strong>
            <p className="pf-toast-body">
              {statusMessage}
            </p>
          </div>
          <button 
            onClick={() => setStatusMessage("")}
            className="pf-toast-close-sm"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Success Message Bubble */}
      {successMessage && (
        <div className="status-bubble pf-float-toast pf-toast-success" >
          <span className="pf-icon-lg">✅</span>
          <div className="pf-flex-fill">
            <strong className="pf-toast-title">
              Success
            </strong>
            <p className="pf-toast-body">
              {successMessage}
            </p>
          </div>
          <button 
            onClick={() => setSuccessMessage("")}
            className="pf-toast-close-sm"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Toast Banner - transient action errors */}
      {actionError && (
        <div className="status-bubble pf-float-toast pf-toast-danger" >
          <span className="pf-icon-lg">❌</span>
          <div className="pf-flex-fill">
            <strong className="pf-toast-title">
              Error
            </strong>
            <p className="pf-toast-body">
              {actionError}
            </p>
          </div>
          <button
            onClick={() => setActionError("")}
            className="pf-toast-close-sm"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Activation Badge - Show only on own profile */}
      {isOwnProfile && activationStatus && (
        <ActivationBadge
          accountStatus={activationStatus.accountStatus}
          emailVerified={activationStatus.emailVerified}
          adminApprovalStatus={activationStatus.adminApprovalStatus}
          onResendEmail={handleResendEmail}
          username={username}
        />
      )}

      {/* Search Carousel Navigation - Show only when viewing from search (not in embedded mode) */}
      {!embedded && searchResults && currentIndex !== null && (
        <div className="profile-carousel-nav">
          <button 
            className="carousel-back-btn"
            onClick={handleBackToSearch}
            title="Back to Search Results"
          >
            <span className="back-icon">↩</span>
            <span className="back-text">Back to Search</span>
          </button>
          
          <div className="carousel-controls">
            <button 
              className="carousel-nav-btn carousel-prev"
              onClick={handlePreviousProfile}
              disabled={currentIndex === 0}
              title="Previous Profile"
            >
              <span className="carousel-arrow">‹‹</span>
              <span className="carousel-label">Previous</span>
            </button>
            
            <span className="carousel-position">
              {currentIndex + 1} / {searchResults.length}
            </span>
            
            <button 
              className="carousel-nav-btn carousel-next"
              onClick={handleNextProfile}
              disabled={currentIndex === searchResults.length - 1}
              title="Next Profile"
            >
              <span className="carousel-label">Next</span>
              <span className="carousel-arrow">››</span>
            </button>
          </div>
        </div>
      )}

      {/* Contribution Reminder Banner — eligibility & dismissal shared via ContributionContext.
          Shown only on OTHER users' profiles (not own). Clicking 'Contribute Now'
          opens the inline popup so the user stays on the profile. */}
      {!isOwnProfile && shouldShowContribution && (
        <div className="contribution-reminder-banner" role="complementary" aria-label="Contribution reminder">
          <span className="contribution-reminder-text">
            {needsActivation
              ? '🔓 Unlock member features — activate your membership!'
              : '🙏 Help keep this platform free — your support makes a difference!'}
          </span>
          <button
            className="contribution-reminder-btn"
            onClick={openPopup}
          >
            {needsActivation ? 'Activate membership' : 'Contribute Now'}
          </button>
          <button
            className="contribution-reminder-close"
            onClick={dismissBanner}
            title="Dismiss"
            aria-label="Dismiss contribution reminder"
          >
            ✕
          </button>
        </div>
      )}

      <div className="profile-header">
        <div className={`profile-header-inner ${isOwnProfile ? 'profile-header-inner-own' : ''}`}>
          {/* Profile Avatar - Always shown */}
          <div className="profile-avatar-container">
            <div className="profile-avatar-image-shell">
              {/* Main Avatar - Always use image[0] (profile picture) */}
              {(() => {
              // Check per-image access for avatar (first image, index 0)
              const avatarAccessInfo = accessibleImages.find(img => img.imageOrder === 0);
              // If accessibleImages is empty (not loaded yet), default to false for non-owners
              const hasAvatarAccess = accessibleImages.length > 0 
                ? (avatarAccessInfo?.hasAccess ?? false)
                : false;
              
              // Check if profile picture is visible due to global setting (backend sets profilePicVisible: true)
              const profilePicVisibleGlobal = user.profilePicVisible === true;
              
              // ALSO check if images array has content - if backend sent images, they should be visible
              const hasImagesFromBackend = user.images && user.images.length > 0 && !user.imagesMasked;
              
              // Avatar should ALWAYS be image[0] (the profile picture)
              // Show it clearly if: own profile, admin, has per-image access, global setting, OR backend sent unmasked images
              const viewerHasAvatarAccess = (isOwnProfile || isAdmin || (piiAccess.images && hasAvatarAccess) || profilePicVisibleGlobal || hasImagesFromBackend);
              const avatarSrc = user.images?.[0]; // Always use image[0] as avatar
              const canOpenAvatar = viewerHasAvatarAccess && !!avatarSrc;

              return (
                <div className={`profile-avatar-frame${canOpenAvatar ? ' is-clickable' : ''}`}
                onClick={() => {
                  if (canOpenAvatar) {
                    openLightbox(avatarSrc, user?.images?.filter(Boolean) || []);
                  }
                }}
                title={canOpenAvatar ? 'Click to enlarge' : ''}
                >
                  {/* Show actual image if own profile, has access, or is admin */}
                  {(canOpenAvatar && avatarSrc) ? (
                    <>
                      <img
                        src={getAuthenticatedImageUrl(avatarSrc)}
                        alt=""
                        aria-hidden="true"
                        className="profile-avatar-img profile-avatar-img-fill"
                      />
                      <img
                        src={getAuthenticatedImageUrl(avatarSrc)}
                        alt={user.firstName}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        className="profile-avatar-img profile-avatar-img-fg"
                      />
                    </>
                  ) : user.images?.[0] ? (
                    /* Show blurred image if user has photos but viewer has no access */
                    <div className="profile-avatar-blur-wrap">
                      <img 
                        src={getAuthenticatedImageUrl(user.images[0])} 
                        alt={user.firstName} 
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        className="profile-avatar-img profile-avatar-blur-img" 
                      />
                      <div className="profile-avatar-lock">
                        🔒
                      </div>
                    </div>
                  ) : (
                    /* Show initials if no photos */
                    <span>{user.firstName?.[0]}{user.lastName?.[0]}</span>
                  )}
                </div>
              );
              })()}
            </div>

          </div>
          
          {/* Profile Info */}
          <div className="profile-title-section">
            <h2>
              {user.firstName} {user.lastName}
              {!isOwnProfile && (
                <span className={`status-bulb-profile ${isOnline ? 'online' : 'offline'}`} title={isOnline ? 'Online Now' : 'Offline'}>
                  {isOnline ? '🟢' : '⚪'}
                </span>
              )}
              {/* Pause Status Badge */}
              {!isOwnProfile && user.accountStatus === 'paused' && (
                <span className="pause-badge-profile" title="User is taking a break">
                  ⏸️ PAUSED
                </span>
              )}
            </h2>

            {/* Quick Info Pills - Age, Height, Looking-for */}
            {(age || (user.birthMonth && user.birthYear) || user.height || user.profileCreatedBy || !isOwnProfile) && (
              <div className="profile-quick-pills">
                {age && (
                  <span
                    className="profile-pill"
                    title={user.birthMonth && user.birthYear
                      ? `Born ${String(user.birthMonth).padStart(2, '0')}/${user.birthYear}`
                      : undefined}
                  >
                    <span className="pill-icon">🎂</span>
                    <span className="pill-value">{age} yrs</span>
                  </span>
                )}
                {user.height && (
                  <span className="profile-pill">
                    <span className="pill-icon">📏</span>
                    <span className="pill-value">{user.height}</span>
                  </span>
                )}
                {(() => {
                  const lookingForSummary = generateLookingForSummary(user);
                  return lookingForSummary ? (
                    <span className="profile-pill">
                      <span className="pill-icon">💍</span>
                      <span className="pill-value">{lookingForSummary}</span>
                    </span>
                  ) : null;
                })()}
                {/* Last Active pill - only for other users' profiles */}
                {!isOwnProfile && user.status?.last_seen && (() => {
                  const badge = getActivityBadgeProps(user.status.last_seen);
                  return (
                    <span className="profile-pill" title={`Last seen: ${getRelativeActivityTime(user.status.last_seen)}`}>
                      <span className="pill-icon">{badge.icon}</span>
                      <span className="pill-value" style={{ color: badge.color }}>{badge.label}</span>
                    </span>
                  );
                })()}
                {/* Profile Creator Badge — right after the activity pill */}
                {user.profileCreatedBy && (
                  <ProfileCreatorBadge
                    creatorType={user.profileCreatedBy}
                    size="medium"
                    showLabel={true}
                    showIcon={true}
                  />
                )}
                {/* Member-view actions inline with the pills: trust badges + share + activity */}
                {!isOwnProfile && (
                  <div className="profile-action-group member-action-group">
                    {trustRowJSX}
                    {shareMenuJSX}
                    {activityButtonJSX}
                  </div>
                )}
              </div>
            )}
            {/* Identity line — compact microtext for own profile, pills for member view */}
            {isOwnProfile ? (
              <div className="profile-identity-row profile-identity-compact">
                {user.username && (
                  <span className="id-handle">@{user.username}</span>
                )}
                {user.profileId && (
                  <>
                    <span className="id-dot">·</span>
                    <span className="id-value">ID <strong>{user.profileId}</strong></span>
                  </>
                )}
                {getProfileUpdatedText(user.updatedAt) && (
                  <>
                    <span className="id-dot">·</span>
                    <span
                      className="id-updated"
                      title={`Updated: ${formatFullDateTime(user.updatedAt)}`}
                    >
                      🕐 updated {getProfileUpdatedText(user.updatedAt)}
                    </span>
                  </>
                )}
              </div>
            ) : null}

            {/* Meta strip — identity | updated | last active | view metrics in one tiny pill row */}
            {!isOwnProfile && ((isAdmin && user.username) || user.profileId || getProfileUpdatedText(user.updatedAt) || lastActiveAt || (currentUsername && currentUsername !== username && (viewerViewMetrics.lastViewedAt || viewerViewMetrics.viewCount > 0))) && (
              <div className="profile-meta-strip">
                {isAdmin && user.username && (
                  <span className="meta-strip-item id-handle">@{user.username}</span>
                )}
                {user.profileId && (
                  <span className="meta-strip-item id-value">ID <strong>{user.profileId}</strong></span>
                )}
                {getProfileUpdatedText(user.updatedAt) && (
                  <span
                    className="meta-strip-item"
                    title={`Updated: ${formatFullDateTime(user.updatedAt)}`}
                  >
                    🕐 updated {getProfileUpdatedText(user.updatedAt)}
                  </span>
                )}
                {lastActiveAt && (
                  <span
                    className="meta-strip-item"
                    title={`Last active: ${formatFullDateTime(lastActiveAt)}`}
                  >
                    👁️ {formatCompactDateTime(lastActiveAt)}
                  </span>
                )}
                {currentUsername && currentUsername !== username && (viewerViewMetrics.lastViewedAt || viewerViewMetrics.viewCount > 0) && (
                  <span className="meta-strip-item">
                    Last viewed: {formatPacificDateTime(viewerViewMetrics.lastViewedAt)} • Viewed: {viewerViewMetrics.viewCount} {viewerViewMetrics.viewCount === 1 ? 'time' : 'times'}
                  </span>
                )}
              </div>
            )}
            
            {/* Account Status Indicator - Show only for own profile if not fully activated */}
            {isOwnProfile && activationStatus && activationStatus.accountStatus !== 'active' && (
              <div style={{
                marginTop: '8px',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                display: 'inline-block',
                backgroundColor: activationStatus.accountStatus === 'pending_email_verification' 
                  ? 'var(--warning-light)' 
                  : activationStatus.accountStatus === 'pending_admin_approval'
                  ? 'var(--info-light)'
                  : 'var(--danger-light)',
                color: activationStatus.accountStatus === 'pending_email_verification'
                  ? 'var(--warning-dark)'
                  : activationStatus.accountStatus === 'pending_admin_approval'
                  ? 'var(--info-color)'
                  : 'var(--danger-color)',
                border: `1px solid ${
                  activationStatus.accountStatus === 'pending_email_verification'
                    ? 'var(--warning-color)'
                    : activationStatus.accountStatus === 'pending_admin_approval'
                    ? 'var(--info-color)'
                    : 'var(--danger-color)'
                }`
              }}>
                {activationStatus.accountStatus === 'pending_email_verification' && '📧 Email Verification Pending'}
                {activationStatus.accountStatus === 'pending_admin_approval' && '⏳ Pending Admin Approval'}
                {activationStatus.accountStatus === 'suspended' && '🚫 Account Suspended'}
                {activationStatus.accountStatus === 'deactivated' && '⚪ Account Deactivated'}
              </div>
            )}
            
            {/* Meta Field Badges */}
            {user.visibleMetaFields && (
              <div className="pf-wrap-gap">
                {/* Verification Badges */}
                {user.visibleMetaFields.idVerified && (
                  <span className="meta-badge verification" title="ID Verified">
                    ✓ ID Verified
                  </span>
                )}
                {user.visibleMetaFields.phoneVerified && (
                  <span className="meta-badge verification" title="Phone Verified">
                    📱 Phone
                  </span>
                )}
                {user.visibleMetaFields.emailVerified && (
                  <span className="meta-badge verification" title="Email Verified">
                    📧 Email
                  </span>
                )}
                {user.visibleMetaFields.employmentVerified && (
                  <span className="meta-badge verification" title="Employment Verified">
                    💼 Employment
                  </span>
                )}
                {user.visibleMetaFields.educationVerified && (
                  <span className="meta-badge verification" title="Education Verified">
                    🎓 Education
                  </span>
                )}
                {user.visibleMetaFields.backgroundCheckStatus === 'passed' && (
                  <span className="meta-badge verification" title="Background Checked">
                    🛡️ Verified
                  </span>
                )}
                
                {/* Premium Badges */}
                {user.visibleMetaFields.isPremium && (
                  <span className="meta-badge premium" title={`${user.visibleMetaFields.premiumStatus} Member`}>
                    💎 {user.visibleMetaFields.premiumStatus === 'vip' ? 'VIP' : user.visibleMetaFields.premiumStatus === 'elite' ? 'Elite' : 'Premium'}
                  </span>
                )}
                {user.visibleMetaFields.isFeatured && (
                  <span className="meta-badge featured" title="Featured Profile">
                    🚀 Featured
                  </span>
                )}
                {user.visibleMetaFields.isStaffPick && (
                  <span className="meta-badge staff-pick" title="Staff Pick">
                    🎖️ Staff Pick
                  </span>
                )}
                
                {/* Achievement Badges */}
                {user.visibleMetaFields.profileRank && (
                  <span className="meta-badge achievement" title="Profile Rank">
                    🏆 {user.visibleMetaFields.profileRank}
                  </span>
                )}
                {user.visibleMetaFields.trustScore >= 80 && (
                  <span className="meta-badge trust" title={`Trust Score: ${user.visibleMetaFields.trustScore}`}>
                    ⭐ Trusted
                  </span>
                )}
              </div>
            )}

          </div>

          {isOwnProfile && (
            <div className="profile-own-actions">
              {/* Compact stat chips */}
              <div className="profile-stat-chips">
                <span className="profile-stat-chip" title="Profile views">👁️ {kpiStats.profileViews}</span>
                <span className="profile-stat-chip" title="Shortlisted by">📝 {kpiStats.shortlistedBy}</span>
                <span className="profile-stat-chip" title="Favorited by">❤️ {kpiStats.favoritedBy}</span>
              </div>

              {/* Uniform action group: trust badges / Edit / Share / Activity */}
              <div className="profile-action-group">
                {trustRowJSX}
                <button
                  className="btn-edit-profile"
                  onClick={handleEditProfile}
                  title="Edit Profile"
                  aria-label="Edit Profile"
                >
                  <span>✏️</span>
                  <span className="btn-edit-label">Edit</span>
                </button>
                {shareMenuJSX}
                {activityButtonJSX}
              </div>
            </div>
          )}

          {/* Referred By Section - Admin Only */}
          {isAdmin && (user.referredByInfo || user.invitedBy) && (
            <div className="referred-by-section">
              <div className="referred-by-header">
                <span className="referred-by-icon">🤝</span>
                <span className="referred-by-title">Referred By:</span>
              </div>
              <div className="referred-by-details">
                {user.referredByInfo && (
                  <>
                    {(user.referredByInfo.firstName || user.referredByInfo.lastName) && (
                      <span className="referred-by-item">
                        <strong>Name:</strong> {[user.referredByInfo.firstName, user.referredByInfo.lastName].filter(Boolean).join(' ')}
                      </span>
                    )}
                    {user.referredByInfo.phone && (
                      <span className="referred-by-item">
                        <strong>Phone:</strong> {user.referredByInfo.phone}
                      </span>
                    )}
                    {user.referredByInfo.email && (
                      <span className="referred-by-item">
                        <strong>Email:</strong> {user.referredByInfo.email}
                      </span>
                    )}
                  </>
                )}
                {user.invitedBy && user.invitedBy !== 'system' && (
                  <span className="referred-by-item">
                    <strong>Invited by:</strong> @{user.invitedBy}
                  </span>
                )}
                {user.invitedBy === 'system' && !user.referredByInfo && (
                  <span className="referred-by-item referred-by-muted">No referral info available</span>
                )}
              </div>
            </div>
          )}

          {/* Member View: Photo Gallery (5 slots) */}
          {!isOwnProfile && (
            <div className="member-photo-gallery-wrapper">
              <div className="member-photo-gallery">
                {/* Slot 1: Profile Picture (always shown if available) */}
                <div className="gallery-slot profile-pic-slot">
                  {user.images?.[0] ? (
                    <img 
                      src={getAuthenticatedImageUrl(user.images[0])}
                      alt="Profile"
                      className="gallery-image"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      onClick={() => {
                        openLightbox(user.images[0], user.images.filter(Boolean));
                      }}
                    />
                  ) : (
                    <div className="gallery-placeholder">
                      <span>👤</span>
                    </div>
                  )}
                  <span className="slot-label">Profile</span>
                </div>
                
                {/* Slots 2-5: Additional Photos (visible if in user.images array from backend) */}
                {[1, 2, 3, 4].map((index) => {
                  // Backend already filters images based on 3-bucket visibility + PII access
                  // If image exists in user.images, it's accessible
                  const image = user.images?.[index];
                  const hasAccess = !!image; // If backend returned it, user has access
                  
                  return (
                    <div key={index} className={`gallery-slot ${hasAccess ? 'accessible' : 'locked'}`}>
                      {hasAccess && image ? (
                        <img 
                          src={getAuthenticatedImageUrl(image)}
                          alt={`Gallery item ${index + 1}`}
                          className="gallery-image"
                          loading="lazy"
                          decoding="async"
                          onClick={() => {
                            openLightbox(image, user.images.filter(Boolean));
                          }}
                        />
                      ) : hasAccess && !image ? (
                        <div className="gallery-placeholder empty">
                          <span>📷</span>
                        </div>
                      ) : (
                        <div className="gallery-placeholder locked" title="Request photo access to view">
                          <span>🔒</span>
                        </div>
                      )}
                      <span className="slot-label">{hasAccess ? (image ? `Photo ${index + 1}` : 'Empty') : 'Locked'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      
      {/* Bio / Tagline */}
      {user.bio && (
        <div className="profile-section profile-bio-section">
          <p className="profile-bio-text">"{user.bio}"</p>
        </div>
      )}

      {/* About Me Narrative */}
      <div className="profile-section profile-narrative-section">
        <div className="section-header-with-edit">
          <h3>📝 About Me</h3>
          {isOwnProfile && !isEditingAboutMe && (
            <button 
              className="about-me-edit-btn"
              onClick={handleEditAboutMe}
              title="Edit About Me"
            >
              ✎
            </button>
          )}
          {isOwnProfile && isEditingAboutMe && (
            <div className="about-me-action-btns">
              <button 
                className="about-me-save-btn"
                onClick={handleSaveAboutMe}
                disabled={savingAboutMe}
                title="Save"
              >
                {savingAboutMe ? '⏳' : '✓'}
              </button>
              {user.customAboutMe && (
                <button 
                  className="about-me-reset-btn"
                  onClick={handleResetAboutMe}
                  disabled={savingAboutMe}
                  title="Reset to auto-generated"
                >
                  ↺
                </button>
              )}
              <button 
                className="about-me-cancel-btn"
                onClick={() => setIsEditingAboutMe(false)}
                disabled={savingAboutMe}
                title="Cancel"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        
        {isEditingAboutMe ? (
          <div className="about-me-edit-container">
            <RichTextEditor
              value={editedAboutMe}
              onChange={setEditedAboutMe}
              placeholder="Write about yourself... Use the toolbar to add bold, colors, and more!"
              minHeight={200}
              simpleToolbar={true}
            />
            
            {/* AI Rephrase Section */}
            <div className="ai-rephrase-section">
              <div className="ai-rephrase-controls">
                <select 
                  value={aiProvider} 
                  onChange={(e) => setAiProvider(e.target.value)}
                  className="ai-provider-select"
                  disabled={isRephrasing}
                  title="Choose AI provider"
                >
                  <option value="groq">⚡ Groq (Recommended)</option>
                  <option value="gemini">🌐 Gemini</option>
                </select>
                <select 
                  value={rephraseStyle} 
                  onChange={(e) => setRephraseStyle(e.target.value)}
                  className="ai-style-select"
                  disabled={isRephrasing}
                >
                  <option value="concise">✂️ Concise & Sharp</option>
                  <option value="warm">🌟 Warm & Friendly</option>
                  <option value="professional">💼 Professional</option>
                  <option value="casual">😊 Casual</option>
                </select>
                <button 
                  className="btn-ai-rephrase"
                  onClick={handleRephraseWithAI}
                  disabled={isRephrasing || !editedAboutMe || editedAboutMe.trim().length < 20}
                  title="Rephrase your text using AI"
                >
                  {isRephrasing ? '✨ Rephrasing...' : '✨ Rephrase with AI'}
                </button>
              </div>
              <p className="ai-rephrase-hint">
                💡 AI will rephrase your text while keeping all facts intact
              </p>
            </div>
          </div>
        ) : (
          <div 
            className="profile-narrative-content"
            dangerouslySetInnerHTML={{ __html: user.customAboutMe || generateAboutMe(user) }}
          />
        )}
      </div>

      {/* Basic Info (Collapsible) */}
      <div className="profile-section">
        <div className="section-header-with-edit">
          <h3 onClick={() => toggleSection('basicInfo')} className="pf-icon-action">
            <span>{collapsedSections.basicInfo ? '▶' : '▼'}</span>
            👤 Basic Information
          </h3>
          {isOwnProfile && editingSection === 'basic' && (
            <div className="pf-btn-row">
              <button 
                className="btn-save-section"
                onClick={() => handleSaveEdit('basic')}
                disabled={savingSection === 'basic'}
              >
                {savingSection === 'basic' ? '💾 Saving...' : '💾 Save'}
              </button>
              <button 
                className="btn-cancel-section"
                onClick={handleCancelEdit}
                disabled={savingSection === 'basic'}
              >
                ✕ Cancel
              </button>
            </div>
          )}
        </div>
        {!collapsedSections.basicInfo && (editingSection === 'basic' ? (
          <div className="inline-edit-form">
            <div className="form-row">
              <div className="form-group">
                <label><strong>Gender:</strong></label>
                <select name="gender" value={editFormData.gender || ''} onChange={handleEditChange} className="form-control">
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label><strong>Height:</strong></label>
                <input type="text" name="height" value={editFormData.height || ''} onChange={handleEditChange} className="form-control" placeholder="e.g. 5'8&quot;" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label><strong>Location:</strong></label>
                <input type="text" name="location" value={editFormData.location || ''} onChange={handleEditChange} className="form-control" placeholder="City, Country" />
              </div>
              <div className="form-group">
                <label><strong>Religion:</strong></label>
                <select name="religion" value={editFormData.religion || ''} onChange={handleEditChange} className="form-control">
                  <option value="">Select Religion</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Christian">Christian</option>
                  <option value="Sikh">Sikh</option>
                  <option value="Buddhist">Buddhist</option>
                  <option value="Jain">Jain</option>
                  <option value="Jewish">Jewish</option>
                  <option value="Parsi">Parsi</option>
                  <option value="No Religion">No Religion</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label><strong>Relationship Status:</strong></label>
                <select name="relationshipStatus" value={editFormData.relationshipStatus || ''} onChange={handleEditChange} className="form-control">
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                  <option value="Separated">Separated</option>
                </select>
              </div>
              <div className="form-group">
                <label><strong>Looking For:</strong></label>
                <select name="lookingFor" value={editFormData.lookingFor || ''} onChange={handleEditChange} className="form-control">
                  <option value="">Select...</option>
                  <option value="Marriage">Marriage</option>
                  <option value="Life Partner">Life Partner</option>
                  <option value="Serious Relationship">Serious Relationship</option>
                  <option value="Casual Dating">Casual Dating</option>
                  <option value="Friendship">Friendship</option>
                </select>
              </div>
            </div>
            {/* Working Status is auto-computed from work experience - see Qualifications tab */}
            <div className="form-row">
              <div className="form-group">
                <label><strong>Citizenship Status:</strong></label>
                <select name="citizenshipStatus" value={editFormData.citizenshipStatus || ''} onChange={handleEditChange} className="form-control">
                  <option value="">Select...</option>
                  <option value="Citizen">Citizen</option>
                  <option value="Greencard">Greencard</option>
                  <option value="Work Visa">Work Visa</option>
                  <option value="Student Visa">Student Visa</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        ) : (
          <div className="profile-info">
            {isOwnProfile && user.username && <p><strong>Username:</strong> {user.username}</p>}
            {(user.gender || user.sex) && <p><strong>Gender:</strong> {user.gender || user.sex}</p>}
            {age && <p><strong>Age:</strong> {age} years</p>}
            {(user.birthMonth && user.birthYear) && <p><strong>Date of Birth:</strong> {String(user.birthMonth).padStart(2, '0')}/{user.birthYear}</p>}
            {user.height && <p><strong>Height:</strong> {user.height}</p>}
            {user.profileCreatedBy && (
              <p>
                <strong>Profile Created By:</strong>{' '}
                {user.profileCreatedBy === 'me' && <span>{'\u{1F464}'} Self</span>}
                {user.profileCreatedBy === 'parent' && <span>{'\u{1F468}\u200D\u{1F469}\u200D\u{1F467}'} Parent/Guardian</span>}
                {user.profileCreatedBy === 'other' && <span>{'\u{1F91D}'} Other (Relative/Friend)</span>}
              </p>
            )}
            {user.location && <p><strong>Location:</strong> {user.location}</p>}
            {user.religion && <p><strong>Religion:</strong> {user.religion}</p>}
            {user.relationshipStatus && <p><strong>Relationship Status:</strong> {user.relationshipStatus}</p>}
            {user.lookingFor && <p><strong>Looking For:</strong> {user.lookingFor}</p>}
            {getWorkingStatus(user) && <p><strong>Working Status:</strong> {getWorkingStatus(user)}</p>}
            {user.linkedinUrl && <p><strong>LinkedIn:</strong> <a href={user.linkedinUrl.match(/^https?:\/\//) ? user.linkedinUrl : `https://${user.linkedinUrl}`} target="_blank" rel="noopener noreferrer">LinkedIn Profile ↗</a></p>}
            {user.citizenshipStatus && <p><strong>Citizenship Status:</strong> {user.citizenshipStatus}</p>}
          </div>
        ))}
      </div>

      {/* What You're Looking For Narrative */}
      <div className="profile-section profile-narrative-section">
        <div className="section-header-with-edit">
          <h3>💑 What You're Looking For</h3>
          {isOwnProfile && !isEditingPartnerPref && (
            <button 
              className="about-me-edit-btn"
              onClick={handleEditPartnerPref}
              title="Edit What You're Looking For"
            >
              ✎
            </button>
          )}
          {isOwnProfile && isEditingPartnerPref && (
            <div className="about-me-action-btns">
              <button 
                className="about-me-save-btn"
                onClick={handleSavePartnerPref}
                disabled={savingPartnerPref}
                title="Save"
              >
                {savingPartnerPref ? '⏳' : '✓'}
              </button>
              {user.customPartnerPreference && (
                <button 
                  className="about-me-reset-btn"
                  onClick={handleResetPartnerPref}
                  disabled={savingPartnerPref}
                  title="Reset to auto-generated"
                >
                  ↺
                </button>
              )}
              <button 
                className="about-me-cancel-btn"
                onClick={() => setIsEditingPartnerPref(false)}
                disabled={savingPartnerPref}
                title="Cancel"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        
        {isEditingPartnerPref ? (
          <div className="about-me-edit-container">
            <RichTextEditor
              value={editedPartnerPref}
              onChange={setEditedPartnerPref}
              placeholder="Describe what you're looking for in a partner..."
              minHeight={200}
              simpleToolbar={true}
            />
          </div>
        ) : (
          <div 
            className="profile-narrative-content"
            dangerouslySetInnerHTML={{ __html: user.customPartnerPreference || generatePartnerPreference(user) }}
          />
        )}
      </div>

      {/* Regional & Cultural Information */}
      {(user.countryOfOrigin || user.countryOfResidence || user.state || user.languagesSpoken?.length > 0 || user.motherTongue || user.caste || user.familyType || user.familyValues || user.castePreference || user.eatingPreference) && (
        <div className="profile-section">
          <div className="section-header-with-edit">
            <h3 onClick={() => toggleSection('regionalCultural')} className="pf-icon-action">
              <span>{collapsedSections.regionalCultural ? '▶' : '▼'}</span>
              🌍 Regional & Cultural
            </h3>
          </div>
          {!collapsedSections.regionalCultural && (
          <div className="profile-info">
            {user.countryOfOrigin && <p><strong>Country of Origin:</strong> {user.countryOfOrigin === 'IN' ? 'India' : user.countryOfOrigin === 'US' ? 'USA' : user.countryOfOrigin}</p>}
            {user.countryOfResidence && <p><strong>Residence:</strong> {user.countryOfResidence === 'IN' ? 'India' : user.countryOfResidence === 'US' ? 'USA' : user.countryOfResidence}</p>}
            {user.state && <p><strong>State:</strong> {user.state}</p>}
            {user.languagesSpoken && user.languagesSpoken.length > 0 && (
              <p><strong>Languages Spoken:</strong> {user.languagesSpoken.join(', ')}</p>
            )}
            {user.motherTongue && <p><strong>Mother Tongue:</strong> {user.motherTongue}</p>}
            {user.caste && <p><strong>Caste:</strong> {user.caste}</p>}
            {user.castePreference && <p><strong>Caste Details:</strong> {user.castePreference}</p>}
            {user.familyType && <p><strong>Family Type:</strong> {user.familyType}</p>}
            {user.familyValues && <p><strong>Family Values:</strong> {user.familyValues}</p>}
          </div>
          )}
        </div>
      )}

      {/* Personal & Lifestyle */}
      {(user.relationshipStatus || user.lookingFor || user.bodyType || user.drinking || user.smoking || user.eatingPreference || user.hasChildren || user.wantsChildren || user.pets || user.interests || user.languages) && (
        <div className="profile-section">
          <div className="section-header-with-edit">
            <h3 onClick={() => toggleSection('personalLifestyle')} className="pf-icon-action">
              <span>{collapsedSections.personalLifestyle ? '▶' : '▼'}</span>
              💭 Personal & Lifestyle
            </h3>
          </div>
          {!collapsedSections.personalLifestyle && (
          <div className="profile-info">
            {user.relationshipStatus && <p><strong>Relationship Status:</strong> {user.relationshipStatus}</p>}
            {user.lookingFor && <p><strong>Looking For:</strong> {user.lookingFor}</p>}
            {user.bodyType && <p><strong>Body Type:</strong> {user.bodyType}</p>}
            {user.drinking && <p><strong>Drinking:</strong> {user.drinking}</p>}
            {user.smoking && <p><strong>Smoking:</strong> {user.smoking}</p>}
            {user.eatingPreference && <p><strong>Eating Preference:</strong> {user.eatingPreference}</p>}
            {user.hasChildren && <p><strong>Has Children:</strong> {user.hasChildren}</p>}
            {user.wantsChildren && <p><strong>Wants Children:</strong> {user.wantsChildren}</p>}
            {user.pets && <p><strong>Pets:</strong> {user.pets}</p>}
            {user.interests && <p><strong>Interests & Hobbies:</strong> {user.interests}</p>}
            {user.languages && <p><strong>Languages:</strong> {user.languages}</p>}
          </div>
          )}
        </div>
      )}

      {/* Education History */}
      {user.educationHistory && user.educationHistory.length > 0 && (
        <div className="profile-section">
          <div className="section-header-with-edit">
            <h3 onClick={() => toggleSection('educationHistory')} className="pf-icon-action">
              <span>{collapsedSections.educationHistory ? '▶' : '▼'}</span>
              🎓 Education History
            </h3>
          </div>
          {!collapsedSections.educationHistory && (
          <div className="profile-info">
            {user.educationHistory.map((edu, idx) => (
              <div key={idx} className="pf-edu-item">
                <p><strong>{edu.level ? `${edu.level} - ${edu.degree}` : edu.degree}</strong></p>
                <p className="pf-muted-suffix">{edu.institution}</p>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Work Experience */}
      {user.workExperience && user.workExperience.length > 0 && (
        <div className="profile-section">
          <div className="section-header-with-edit">
            <h3 onClick={() => toggleSection('workExperience')} className="pf-icon-action">
              <span>{collapsedSections.workExperience ? '▶' : '▼'}</span>
              💼 Work Experience
            </h3>
          </div>
          {!collapsedSections.workExperience && (
          <div className="profile-info">
            {user.workExperience.map((work, idx) => (
              <div key={idx} className="pf-edu-item">
                <p><strong>{work.status === 'current' ? '🟢 Current Position' : '⚪ Past Position'}</strong></p>
                <p className="pf-muted-suffix">{work.description}</p>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Contact Information (PII Protected) */}
      <div className="profile-section">
        <div className="section-header-with-edit">
          <h3 onClick={() => toggleSection('contactInformation')} className="pf-icon-action">
            <span>{collapsedSections.contactInformation ? '▶' : '▼'}</span>
            📧 Contact Information
          </h3>
          {isOwnProfile && editingSection === 'contact' && (
            <div className="pf-btn-row">
              <button 
                className="btn-save-section"
                onClick={() => handleSaveEdit('contact')}
                disabled={savingSection === 'contact'}
              >
                {savingSection === 'contact' ? '💾 Saving...' : '💾 Save'}
              </button>
              <button 
                className="btn-cancel-section"
                onClick={handleCancelEdit}
                disabled={savingSection === 'contact'}
              >
                ✕ Cancel
              </button>
            </div>
          )}
        </div>
        {!collapsedSections.contactInformation && (
        <>
        {/* Show contact info if: own profile, has PII access, OR contact fields are member-visible */}
        {isOwnProfile || piiAccess.contact_info || user.contactNumberVisible || user.contactEmailVisible || user.linkedinUrlVisible ? (
          editingSection === 'contact' ? (
            <div className="inline-edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label><strong>Contact Number:</strong></label>
                  <input type="tel" name="contactNumber" value={editFormData.contactNumber || ''} onChange={handleEditChange} className="form-control" placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group">
                  <label><strong>Contact Email:</strong></label>
                  <input type="email" name="contactEmail" value={editFormData.contactEmail || ''} onChange={handleEditChange} className="form-control" placeholder="email@example.com" />
                </div>
              </div>
            </div>
          ) : (
            <div className="profile-info">
              {/* Show contact numbers (multi-contact support) */}
              {user.contactNumbers && Array.isArray(user.contactNumbers) && user.contactNumbers.length > 0 ? (
                user.contactNumbers.map((contact, idx) => {
                  const entryVisible = contact.visible !== false;
                  const canSee = (entryVisible && user.contactNumberVisible !== false) || piiAccess.contact_number || isOwnProfile;
                  const isMasked = contact.number && contact.number.includes('***');
                  if (canSee && contact.number && !isMasked) {
                    return (
                      <p key={idx}>
                        <strong>Phone ({contact.label || 'primary'}):</strong> {contact.number}
                        {entryVisible && !isOwnProfile && <span className="member-visible-tag">👁️ Member Visible</span>}
                        {!entryVisible && isOwnProfile && <span className="pf-mini-tag">🔒 Hidden</span>}
                      </p>
                    );
                  } else if (contact.number) {
                    return (
                      <p key={idx}>
                        <strong>Phone ({contact.label || 'primary'}):</strong> <span className="pii-masked">🔒 Private</span>
                        {idx === 0 && (
                          <button 
                            className="btn-request-inline" 
                            onClick={() => setShowPIIRequestModal(true)}
                           
                          >
                            Request Access
                          </button>
                        )}
                      </p>
                    );
                  }
                  return null;
                })
              ) : (
                /* Backward compat: single contactNumber for old profiles */
                (user.contactNumberVisible || piiAccess.contact_number || isOwnProfile) && user.contactNumber && !user.contactNumberMasked ? (
                  <p>
                    <strong>Contact Number:</strong> {user.contactNumber}
                    {user.contactNumberVisible && !isOwnProfile && <span className="member-visible-tag">👁️ Member Visible</span>}
                  </p>
                ) : user.contactNumber ? (
                  <p>
                    <strong>Contact Number:</strong> <span className="pii-masked">🔒 Private</span>
                    <button 
                      className="btn-request-inline" 
                      onClick={() => setShowPIIRequestModal(true)}
                     
                    >
                      Request Access
                    </button>
                  </p>
                ) : null
              )}
              {/* Show contact email if visible or has access */}
              {(user.contactEmailVisible || piiAccess.contact_email || isOwnProfile) && user.contactEmail && !user.contactEmailMasked ? (
                <p>
                  <strong>Contact Email:</strong> {user.contactEmail}
                  {user.contactEmailVisible && !isOwnProfile && <span className="member-visible-tag">👁️ Member Visible</span>}
                </p>
              ) : user.contactEmail ? (
                <p>
                  <strong>Contact Email:</strong> <span className="pii-masked">🔒 Private</span>
                  <button 
                    className="btn-request-inline" 
                    onClick={() => setShowPIIRequestModal(true)}
                   
                  >
                    Request Access
                  </button>
                </p>
              ) : null}
              {/* Show LinkedIn URL if visible or has access */}
              {user.linkedinUrl && (
                (user.linkedinUrlVisible || piiAccess.linkedin_url || isOwnProfile) && (!user.linkedinUrlMasked || piiAccess.linkedin_url) ? (
                  <p>
                    <strong>LinkedIn:</strong>{' '}
                    <a href={user.linkedinUrl.match(/^https?:\/\//) ? user.linkedinUrl : `https://${user.linkedinUrl}`} target="_blank" rel="noopener noreferrer" className="pf-primary-text">
                      LinkedIn Profile ↗
                    </a>
                    {user.linkedinUrlVisible && !isOwnProfile && <span className="member-visible-tag">👁️ Member Visible</span>}
                  </p>
                ) : (
                  <p>
                    <strong>LinkedIn:</strong> <span className="pii-masked">🔒 Private</span>
                    <button 
                      className="btn-request-inline" 
                      onClick={() => setShowPIIRequestModal(true)}
                     
                    >
                      Request Access
                    </button>
                  </p>
                )
              )}
            </div>
          )
        ) : (
          <div className="pii-locked">
            <div className="lock-icon">🔒</div>
            <p>Contact information is private</p>
            <button
              className="btn-request-small"
              onClick={() => setShowPIIRequestModal(true)}
            >
              Request Access
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Preferences & Background (Collapsible) */}
      <div className="profile-section">
        <div className="section-header-with-edit">
          <h3 onClick={() => toggleSection('preferencesBackground')} className="pf-icon-action">
            <span>{collapsedSections.preferencesBackground ? '▶' : '▼'}</span>
            💭 Preferences & Background
          </h3>
        </div>
        {!collapsedSections.preferencesBackground && (
        <div className="profile-info">
          {/* Removed duplicates: castePreference, eatingPreference, familyType, familyValues - already in Regional & Cultural */}
          {user.familyBackground && <p><strong>Family Background:</strong> {user.familyBackground}</p>}
          {(user.aboutMe || user.aboutYou) && <p><strong>About:</strong> {user.aboutMe || user.aboutYou}</p>}
          {user.partnerPreference && <p><strong>Partner Preference:</strong> {user.partnerPreference}</p>}
        </div>
        )}
      </div>

      {/* Partner Matching Criteria */}
      {user.partnerCriteria && Object.keys(user.partnerCriteria).length > 0 && (
        <div className="profile-section">
          <div className="section-header-with-edit">
            <h3 onClick={() => toggleSection('partnerCriteria')} className="pf-icon-action">
              <span>{collapsedSections.partnerCriteria ? '▶' : '▼'}</span>
              🎯 Partner Matching Criteria
            </h3>
          </div>
          {!collapsedSections.partnerCriteria && (
          <div className="profile-info">
            {user.partnerCriteria.ageRangeRelative ? (
              <p>
                <strong>Preferred Age Range:</strong>{' '}
                {(() => {
                  const userAge = (() => {
                    // FIXED: Use birthMonth and birthYear instead of dateOfBirth
                    if (!user.birthMonth || !user.birthYear) return null;
                    const today = new Date();
                    let age = today.getFullYear() - user.birthYear;
                    // If current month hasn't reached birth month yet, subtract 1
                    if (today.getMonth() + 1 < user.birthMonth) {
                      age--;
                    }
                    return age;
                  })();
                  
                  if (!userAge) return 'Not specified';
                  
                  const minAge = userAge + (user.partnerCriteria.ageRangeRelative.minOffset || 0);
                  const maxAge = userAge + (user.partnerCriteria.ageRangeRelative.maxOffset || 0);
                  const minText = user.partnerCriteria.ageRangeRelative.minOffset === 0 ? 'same age' : `${Math.abs(user.partnerCriteria.ageRangeRelative.minOffset)} ${user.partnerCriteria.ageRangeRelative.minOffset < 0 ? 'year(s) younger' : 'year(s) older'}`;
                  const maxText = user.partnerCriteria.ageRangeRelative.maxOffset === 0 ? 'same age' : `${Math.abs(user.partnerCriteria.ageRangeRelative.maxOffset)} ${user.partnerCriteria.ageRangeRelative.maxOffset < 0 ? 'year(s) younger' : 'year(s) older'}`;
                  
                  return `${minAge}-${maxAge} years (${minText} to ${maxText})`;
                })()}
              </p>
            ) : user.partnerCriteria.ageRange && (user.partnerCriteria.ageRange.min || user.partnerCriteria.ageRange.max) && (
              <p>
                <strong>Preferred Age Range:</strong>{' '}
                {user.partnerCriteria.ageRange.min || 'Any'} - {user.partnerCriteria.ageRange.max || 'Any'} years
              </p>
            )}
            {user.partnerCriteria.heightRangeRelative ? (
              <p>
                <strong>Preferred Height Range:</strong>{' '}
                {(() => {
                  if (!user.height) return 'Not specified';
                  
                  // Parse user's height (e.g., "5'8\"" or "5'8")
                  const heightMatch = user.height.match(/(\d+)'(\d+)/);
                  if (!heightMatch) return 'Not specified';
                  
                  const userHeightInches = (parseInt(heightMatch[1]) * 12) + parseInt(heightMatch[2]);
                  const minInches = userHeightInches + (user.partnerCriteria.heightRangeRelative.minInches || 0);
                  const maxInches = userHeightInches + (user.partnerCriteria.heightRangeRelative.maxInches || 0);
                  
                  const minFeet = Math.floor(minInches / 12);
                  const minRemainInches = minInches % 12;
                  const maxFeet = Math.floor(maxInches / 12);
                  const maxRemainInches = maxInches % 12;
                  
                  const minText = user.partnerCriteria.heightRangeRelative.minInches === 0 ? 'same height' : `${Math.abs(user.partnerCriteria.heightRangeRelative.minInches)} in ${user.partnerCriteria.heightRangeRelative.minInches < 0 ? 'shorter' : 'taller'}`;
                  const maxText = user.partnerCriteria.heightRangeRelative.maxInches === 0 ? 'same height' : `${Math.abs(user.partnerCriteria.heightRangeRelative.maxInches)} in ${user.partnerCriteria.heightRangeRelative.maxInches < 0 ? 'shorter' : 'taller'}`;
                  
                  return `${minFeet}'${minRemainInches}" - ${maxFeet}'${maxRemainInches}" (${minText} to ${maxText})`;
                })()}
              </p>
            ) : user.partnerCriteria.heightRange && (
              <>
                {(user.partnerCriteria.heightRange.minFeet || user.partnerCriteria.heightRange.maxFeet) ? (
                  <p>
                    <strong>Preferred Height Range:</strong>{' '}
                    {user.partnerCriteria.heightRange.minFeet ? 
                      `${user.partnerCriteria.heightRange.minFeet}'${user.partnerCriteria.heightRange.minInches || 0}"` : 
                      'Any'} 
                    {' - '}
                    {user.partnerCriteria.heightRange.maxFeet ? 
                      `${user.partnerCriteria.heightRange.maxFeet}'${user.partnerCriteria.heightRange.maxInches || 0}"` : 
                      'Any'}
                  </p>
                ) : (
                  <p><strong>Preferred Height Range:</strong> Not specified</p>
                )}
              </>
            )}
            {user.partnerCriteria.educationLevel && user.partnerCriteria.educationLevel.length > 0 && (
              <p><strong>Preferred Education:</strong> {user.partnerCriteria.educationLevel.join(', ')}</p>
            )}
            {user.partnerCriteria.profession && user.partnerCriteria.profession.length > 0 && (
              <p><strong>Preferred Profession:</strong> {user.partnerCriteria.profession.join(', ')}</p>
            )}
            {user.partnerCriteria.location && (
              <p><strong>Preferred Locations:</strong> {
                Array.isArray(user.partnerCriteria.location) 
                  ? user.partnerCriteria.location.join(', ')
                  : user.partnerCriteria.location
              }</p>
            )}
            {user.partnerCriteria.languages && user.partnerCriteria.languages.length > 0 && (
              <p><strong>Preferred Languages:</strong> {user.partnerCriteria.languages.join(', ')}</p>
            )}
            {user.partnerCriteria.religion && user.partnerCriteria.religion.length > 0 && (
              <p><strong>Preferred Religion:</strong> {user.partnerCriteria.religion.join(', ')}</p>
            )}
            {user.partnerCriteria.caste && (
              <p><strong>Preferred Caste:</strong> {user.partnerCriteria.caste}</p>
            )}
            {user.partnerCriteria.eatingPreference && user.partnerCriteria.eatingPreference.length > 0 && (
              <p><strong>Preferred Eating:</strong> {user.partnerCriteria.eatingPreference.join(', ')}</p>
            )}
            {user.partnerCriteria.familyType && user.partnerCriteria.familyType.length > 0 && (
              <p><strong>Preferred Family Type:</strong> {user.partnerCriteria.familyType.join(', ')}</p>
            )}
            {user.partnerCriteria.familyValues && user.partnerCriteria.familyValues.length > 0 && (
              <p><strong>Preferred Family Values:</strong> {user.partnerCriteria.familyValues.join(', ')}</p>
            )}
            {user.partnerCriteria.relationshipStatus && user.partnerCriteria.relationshipStatus !== 'Any' && (
              <p><strong>Partner's Relationship Status:</strong> {user.partnerCriteria.relationshipStatus}</p>
            )}
            {user.partnerCriteria.lookingFor && user.partnerCriteria.lookingFor !== 'Any' && (
              <p><strong>Partner Looking For:</strong> {user.partnerCriteria.lookingFor}</p>
            )}
            {user.partnerCriteria.bodyType && user.partnerCriteria.bodyType !== 'Any' && (
              <p><strong>Partner's Body Type:</strong> {user.partnerCriteria.bodyType}</p>
            )}
            {user.partnerCriteria.pets && user.partnerCriteria.pets !== 'Any' && (
              <p><strong>Partner's Pets:</strong> {user.partnerCriteria.pets}</p>
            )}
            {user.partnerCriteria.drinking && user.partnerCriteria.drinking !== 'Any' && (
              <p><strong>Partner's Drinking:</strong> {user.partnerCriteria.drinking}</p>
            )}
            {user.partnerCriteria.smoking && user.partnerCriteria.smoking !== 'Any' && (
              <p><strong>Partner's Smoking:</strong> {user.partnerCriteria.smoking}</p>
            )}
            {user.partnerCriteria.hasChildren && user.partnerCriteria.hasChildren !== 'Any' && (
              <p><strong>Partner Has Children:</strong> {user.partnerCriteria.hasChildren}</p>
            )}
            {user.partnerCriteria.wantsChildren && user.partnerCriteria.wantsChildren !== 'Any' && (
              <p><strong>Partner Wants Children:</strong> {user.partnerCriteria.wantsChildren}</p>
            )}
          </div>
          )}
        </div>
      )}

      {/* Profile Images Section */}
      <div className="profile-section">
        <div className="section-header-with-edit">
          <h3>📷 Photos {user.publicImages?.length > 0 && !isOwnProfile && <span className="member-visible-tag">👁️ Member Visible</span>}</h3>
          <div className="pf-btn-row">
            {!isOwnProfile && currentUsername && (
              <button
                onClick={async () => {
                  logger.debug('Manual refresh triggered');
                  try {
                    // Reload profile with requester param to get proper image filtering
                    const response = await api.get(`/profile/${username}?requester=${currentUsername}`);
                    if (response.data) {
                      setUser(response.data);
                      logger.debug('Profile refreshed, hasOnRequestImages:', response.data.hasOnRequestImages);
                    }
                    // Also refresh PII access status
                    await checkPIIAccess();
                    await loadAccessibleImages();
                  } catch (err) {
                    logger.error('Error refreshing:', err);
                  }
                }}
                className="pf-mini-btn"
                title="Refresh image access status"
              >
                🔄 Refresh
              </button>
            )}
          </div>
        </div>
        {/* Photo visibility logic:
            1. Own profile: show all images
            2. Has accessibleImages loaded: use per-image access status (respects one-time views)
            3. Else: show only publicImages + request access button for private ones
        */}
        {isOwnProfile ? (
          user.images?.length > 0 ? (
            <div className="images-grid pf-card-grid" >
              {user.images.map((img, idx) => (
                <div
                  key={`${img}-${idx}`}
                  className="pf-square-media"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    openLightbox(img, user.images.filter(Boolean));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openLightbox(img, user.images.filter(Boolean));
                    }
                  }}
                  aria-label={`View photo ${idx + 1} in full size`}
                >
                  <img
                    src={getAuthenticatedImageUrl(img)}
                    alt=""
                    aria-hidden="true"
                    className="pf-media-img pf-media-img-fill"
                  />
                  <img
                    src={getAuthenticatedImageUrl(img)}
                    alt={`${user.firstName}'s profile ${idx + 1}`}
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="pf-media-img pf-media-img-fg"
                  />
                  {idx === 0 && (
                    <div className="pf-corner-badge">
                      ⭐ Profile Picture
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No photos available</p>
          )
        ) : accessibleImages.length > 0 ? (
          /* Use per-image access status - respects one-time views and expiry */
          <>
            {/* Debug: Show imageReasons and hasOnRequestImages from backend */}
            <div className="pf-field-hint">
              🔍 Debug: {user.images?.length || 0} images, reasons: [{user.imageReasons?.join(', ')}], 
              hasOnRequest: {String(user.hasOnRequestImages)}, count: {user.onRequestImageCount}
            </div>
            <div className="images-grid pf-card-grid" >
              {accessibleImages.filter(img => img.hasAccess).map((image, idx) => (
                <ProfileImage
                  key={image.imageId || idx}
                  image={image}
                  viewerUsername={currentUsername}
                  profileOwnerUsername={username}
                  isFavorited={isFavorited}
                  isShortlisted={isShortlisted}
                  onClick={(imageData) => {
                    const imageUrl = imageData?.url || imageData?.imageUrl || imageData;
                    if (imageUrl && typeof imageUrl === 'string') {
                      const allUrls = accessibleImages.filter(i => i.hasAccess).map(i => i.url || i.imageUrl).filter(Boolean);
                      openLightbox(imageUrl, allUrls);
                    }
                  }}
                />
              ))}
            </div>
            {/* Show request access if user has onRequest images (regardless of accessibleImages status) */}
            {user.hasOnRequestImages && !piiAccess.images && (
              <div className="pii-locked pf-mt-16" >
                <div className="lock-icon">{piiRequestStatus.images === 'pending' ? '⏳' : '🔒'}</div>
                <p>
                  {piiRequestStatus.images === 'pending' 
                    ? 'Request pending approval' 
                    : `${user.onRequestImageCount || 'Some'} photo${(user.onRequestImageCount || 0) !== 1 ? 's' : ''} require${(user.onRequestImageCount || 0) === 1 ? 's' : ''} approval`
                  }
                </p>
                {piiRequestStatus.images !== 'pending' && (
                  <button
                    className="btn-request-small"
                    onClick={() => setShowPIIRequestModal(true)}
                  >
                    Request Access
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Debug: Show imageReasons and hasOnRequestImages from backend */}
            <div className="pf-field-hint">
              🔍 Debug: {user.images?.length || 0} images, reasons: [{user.imageReasons?.join(', ')}], 
              hasOnRequest: {String(user.hasOnRequestImages)}, count: {user.onRequestImageCount}
            </div>
            {publicImageObjects.length > 0 && (
              <div className="images-grid pf-card-grid" >
                {publicImageObjects.map((image, idx) => (
                  <ProfileImage
                    key={image.imageId || idx}
                    image={image}
                    viewerUsername={currentUsername}
                    profileOwnerUsername={username}
                    isFavorited={isFavorited}
                    isShortlisted={isShortlisted}
                    onClick={(imageData) => {
                      const imageUrl = imageData?.url || imageData?.imageUrl || imageData;
                      if (imageUrl && typeof imageUrl === 'string') {
                        const allUrls = publicImageObjects.map(i => i.url || i.imageUrl).filter(Boolean);
                        openLightbox(imageUrl, allUrls);
                      }
                    }}
                  />
                ))}
              </div>
            )}
            {/* Only show request access if user has onRequest images and no access yet */}
            {user.hasOnRequestImages && !piiAccess.images && (
              <div className="pii-locked">
                <div className="lock-icon">{piiRequestStatus.images === 'pending' ? '⏳' : '🔒'}</div>
                <p>
                  {piiRequestStatus.images === 'pending' 
                    ? 'Request pending approval' 
                    : `${user.onRequestImageCount || 'Some'} photo${(user.onRequestImageCount || 0) !== 1 ? 's' : ''} require${(user.onRequestImageCount || 0) === 1 ? 's' : ''} approval`
                  }
                </p>
                {piiRequestStatus.images !== 'pending' && (
                  <button
                    className="btn-request-small"
                    onClick={() => setShowPIIRequestModal(true)}
                  >
                    Request Access
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* L3V3L Matching Breakdown Table */}
      {l3v3lMatchData && !isOwnProfile && (
        <div>
          {l3v3lIsQuickScore && (
            <p className="pf-field-note">
              ~ Approximate score — click the score bubble to calculate deep analysis
            </p>
          )}
          <L3V3LMatchingTable matchingData={l3v3lMatchData} onRefresh={handleRefreshL3v3l} refreshing={l3v3lRefreshing} />
        </div>
      )}

      {/* Edit Profile Button - Show only for own profile */}
      {isOwnProfile && (
        <div className="pf-cta-row">
          <button 
            className="btn-edit-profile pf-cta-btn"
            onClick={() => navigate('/edit-profile')}
            
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            ✏️ Edit Profile
          </button>
        </div>
      )}

      {/* Pause Status Notice */}
      {!isOwnProfile && user.accountStatus === 'paused' && (
        <div className="profile-pause-notice">
          <span className="pause-notice-icon">⏸️</span>
          <div className="pause-notice-content">
            <strong>This user is taking a break</strong>
            <p>
              {user.pauseMessage || 'They have temporarily paused their account. Messaging is disabled, but you can still add them to your favorites or shortlist.'}
            </p>
            {user.pausedUntil && (
              <p className="pause-until">
                Scheduled to return: {new Date(user.pausedUntil).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Sticky Action Buttons - Desktop Only (right side) */}
      {!isOwnProfile && (
        <div className="profile-sticky-actions">
          <button className={`btn-sticky-action btn-action-message ${hasMessages ? 'active' : ''}`} onClick={() => setShowMessageModal(true)} disabled={user.accountStatus === 'paused'} title={user.accountStatus === 'paused' ? 'User is paused - messaging disabled' : (hasMessages ? 'Continue conversation' : 'Send Message')}>
            <span className="action-icon">{hasMessages ? ACTION_ICONS.MESSAGE_ACTIVE : ACTION_ICONS.MESSAGE}</span>
          </button>
          <button className={`btn-sticky-action btn-action-favorite ${isFavorited ? 'active' : ''}`} onClick={handleToggleFavorite} title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
            <span className="action-icon">{isFavorited ? ACTION_ICONS.UNFAVORITE : ACTION_ICONS.FAVORITE}</span>
          </button>
          <button className={`btn-sticky-action btn-action-shortlist ${isShortlisted ? 'active' : ''}`} onClick={handleToggleShortlist} title={isShortlisted ? 'Remove from Shortlist' : 'Add to Shortlist'}>
            <span className="action-icon">{isShortlisted ? ACTION_ICONS.SHORTLIST_ACTIVE : ACTION_ICONS.SHORTLIST}</span>
          </button>
          <button className="btn-sticky-action btn-action-pii" onClick={handleOpenPIIRequest} disabled={hasAllAccess} title={hasAllAccess ? 'You Have All Private Information Access' : 'Request Private Information Access'}>
            <span className="action-icon">{hasAllAccess ? ACTION_ICONS.HAS_ACCESS : ACTION_ICONS.REQUEST_CONTACT}</span>
          </button>
          <button className={`btn-sticky-action btn-action-exclude ${isExcluded ? 'active' : ''}`} onClick={handleToggleExclude} disabled={exclusionLoading} title={isExcluded ? 'Unhide' : 'Hide'}>
            <span className="action-icon">{exclusionLoading ? '⏳' : (isExcluded ? ACTION_ICONS.UNHIDE : ACTION_ICONS.HIDE)}</span>
          </button>
        </div>
      )}

      {/* Action Buttons - Mobile Only (bottom bar) */}
      {!isOwnProfile && (
        <div className="profile-action-buttons">
          <button className={`btn-profile-action btn-micro-primary ${hasMessages ? 'active' : ''}`} onClick={() => setShowMessageModal(true)} disabled={user.accountStatus === 'paused'} title={user.accountStatus === 'paused' ? 'User is paused - messaging disabled' : (hasMessages ? 'Continue conversation' : 'Send Message')}>
            <span className="action-icon">{hasMessages ? ACTION_ICONS.MESSAGE_ACTIVE : ACTION_ICONS.MESSAGE}</span>
            <span className="action-label">{hasMessages ? 'Messages' : 'Message'}</span>
          </button>
          <button className={`btn-profile-action ${isFavorited ? 'btn-micro-success' : 'btn-micro-secondary'}`} onClick={handleToggleFavorite} title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
            <span className="action-icon">{isFavorited ? ACTION_ICONS.UNFAVORITE : ACTION_ICONS.FAVORITE}</span>
            <span className="action-label">{isFavorited ? 'Favorited' : 'Favorite'}</span>
          </button>
          <button className={`btn-profile-action ${isShortlisted ? 'btn-micro-info' : 'btn-micro-secondary'}`} onClick={handleToggleShortlist} title={isShortlisted ? 'Remove from Shortlist' : 'Add to Shortlist'}>
            <span className="action-icon">{isShortlisted ? ACTION_ICONS.SHORTLIST_ACTIVE : ACTION_ICONS.SHORTLIST}</span>
            <span className="action-label">{isShortlisted ? 'Shortlisted' : 'Shortlist'}</span>
          </button>
          <button className="btn-profile-action btn-micro-warning" onClick={handleOpenPIIRequest} disabled={hasAllAccess} title={hasAllAccess ? 'You Have All Private Information Access' : 'Request Private Information Access'}>
            <span className="action-icon">{hasAllAccess ? ACTION_ICONS.HAS_ACCESS : ACTION_ICONS.REQUEST_CONTACT}</span>
            <span className="action-label">{hasAllAccess ? 'Full Access' : 'Request PII'}</span>
          </button>
          <button className={`btn-profile-action ${isExcluded ? 'btn-micro-danger' : 'btn-micro-secondary'}`} onClick={handleToggleExclude} disabled={exclusionLoading} title={isExcluded ? 'Unhide' : 'Hide'}>
            <span className="action-icon">{exclusionLoading ? '⏳' : (isExcluded ? ACTION_ICONS.UNHIDE : ACTION_ICONS.HIDE)}</span>
            <span className="action-label">{isExcluded ? 'Unhide' : 'Hide'}</span>
          </button>
        </div>
      )}

      {/* Share via SMS Section */}
      {!isOwnProfile && (
        <div className="profile-share-sms-section">
          <h4>📱 Share Profile via SMS</h4>

          {/* Contacts List with Share Buttons */}
          <div className="share-contacts-list">
            {currentUserContacts
              .filter(c => c.number) // Show all contacts with phone numbers
              .map((contact, index) => (
                <div key={index} className="share-contact-item">
                  <div className="share-contact-info">
                    <span className="share-contact-label">
                      {contact.label.charAt(0).toUpperCase() + contact.label.slice(1)}
                    </span>
                    {contact.number && (
                      <span className="share-contact-phone">{contact.number}</span>
                    )}
                  </div>
                  <div className="share-contact-actions">
                    <button
                      className="btn-micro btn-micro-warning"
                      onClick={() => {
                        setShareRecipient(contact.label);
                        setSharePhone(contact.number || '');
                        handleEditMessage();
                      }}
                      disabled={!contact.number}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-micro btn-micro-primary"
                      onClick={() => {
                        handleSendSMS(null, contact.label, contact.number || '');
                      }}
                      disabled={shareSending || !contact.number}
                    >
                      {shareSending ? '⏳' : '📤'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

          {/* Add New Contact Row */}
          <div className="add-new-contact-row">
            <select
              className="form-control"
              value={shareRecipient}
              onChange={(e) => handleRecipientChange(e.target.value)}
            >
              <option value="">Select recipient...</option>
              {CONTACT_LABELS.map(label => (
                <option key={label} value={label}>
                  {label.charAt(0).toUpperCase() + label.slice(1)}
                </option>
              ))}
            </select>
            <input
              type="tel"
              className="form-control"
              placeholder="Phone number"
              value={sharePhone}
              onChange={(e) => setSharePhone(e.target.value)}
            />
            <button
              className="btn-micro btn-micro-warning"
              onClick={handleEditMessage}
              disabled={!shareRecipient || !sharePhone}
            >
              ✏️
            </button>
            <button
              className="btn-micro btn-micro-primary"
              onClick={() => handleSendSMS()}
              disabled={shareSending || !shareRecipient || !sharePhone}
            >
              {shareSending ? '⏳' : '📤'}
            </button>
          </div>

          {/* Message Editor Modal */}
          {showMessageEditor && (
            <div className="modal-overlay" onClick={() => setShowMessageEditor(false)}>
              <div className="message-editor-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>✏️ Edit SMS Message</h3>
                  <button className="modal-close" onClick={() => setShowMessageEditor(false)}>✕</button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control message-editor-textarea"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={8}
                    placeholder="Enter your custom message..."
                  />
                  <div className="message-editor-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowMessageEditor(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleSendSMS(customMessage)}
                      disabled={shareSending || !customMessage.trim()}
                    >
                      {shareSending ? '⏳ Sending...' : '📤 Send Custom Message'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shared Profiles History - Shows for any profile being viewed */}
          {profileShares.length > 0 && (
            <div className="profile-shares-history">
              <h5>📋 Recently Shared Profiles</h5>
              <table className="profile-shares-table">
                <thead>
                  <tr>
                    <th>Shared Profile</th>
                    <th>Recipient</th>
                    <th>Phone</th>
                    <th>Sent At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {profileShares.map((share, index) => (
                    <tr key={index}>
                      <td>{share.sharedProfileUsername}</td>
                      <td className="pf-capitalize">{share.recipientType}</td>
                      <td>{share.recipientPhone}</td>
                      <td>{new Date(share.sentAt).toLocaleString()}</td>
                      <td>
                        <div className="pf-center-row">
                          <button
                            className="btn-micro btn-micro-primary"
                            onClick={() => handleReshare(share)}
                            disabled={shareSending}
                            title="Reshare this profile"
                          >
                            📤
                          </button>
                          <DeleteButton
                            onDelete={() => handleDeleteShare(share)}
                            itemName={`share record for ${share.sharedProfileUsername}`}
                            size="small"
                            icon="🗑️"
                            confirmIcon="✓"
                            className="btn-micro btn-micro-danger"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Exclusion Preview Modal */}
      {showExclusionPreview && exclusionPreviewData && (
        <div className="modal-overlay" onClick={() => setShowExclusionPreview(false)}>
          <div className="exclusion-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header pf-danger-head" >
              <h2 className="pf-flush-row">
                🙈 Confirm Hide
              </h2>
              <button 
                className="modal-close pf-modal-close" 
                onClick={() => setShowExclusionPreview(false)}
                
              >
                ✕
              </button>
            </div>
            <div className="modal-body pf-modal-body" >
              <p className="pf-modal-sub">
                Hiding <strong>{user?.firstName || exclusionPreviewData.target_username}</strong> will permanently remove:
              </p>
              
              <div className="pf-modal-panel">
                {exclusionPreviewData.messages_count > 0 && (
                  <div className="pf-kv-row">
                    <span>💬 Messages</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.messages_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.favorites_count > 0 && (
                  <div className="pf-kv-row">
                    <span>❤️ Favorites</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.favorites_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.shortlists_count > 0 && (
                  <div className="pf-kv-row">
                    <span>📋 Shortlists</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.shortlists_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.pii_requests_count > 0 && (
                  <div className="pf-kv-row">
                    <span>📝 PII Requests</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.pii_requests_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.pii_access_count > 0 && (
                  <div className="pf-kv-row">
                    <span>🔓 PII Access</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.pii_access_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.notifications_count > 0 && (
                  <div className="pf-kv-flat">
                    <span>🔔 Pending Notifications</span>
                    <strong className="pf-danger-text">{exclusionPreviewData.notifications_count}</strong>
                  </div>
                )}
                {exclusionPreviewData.total_items === 0 && (
                  <div className="pf-modal-note">
                    No existing data to remove
                  </div>
                )}
              </div>
              
              <p className="pf-modal-hint">
                This action will also notify the user that a profile they were interested in is no longer available.
              </p>
            </div>
            <div className="modal-footer pf-modal-footer" >
              <button 
                onClick={() => setShowExclusionPreview(false)}
                className="pf-btn-outline"
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  try {
                    setExclusionLoading(true);
                    await api.post(`/exclusions/${username}`);
                    setIsExcluded(true);
                    setShowExclusionPreview(false);
                    setExclusionPreviewData(null);
                    setSuccessMessage('✅ Marked as not interested');
                    setTimeout(() => setSuccessMessage(''), 3000);
                  } catch (err) {
                    showActionError('Failed to exclude user');
                  } finally {
                    setExclusionLoading(false);
                  }
                }}
                disabled={exclusionLoading}
                className="pf-btn-danger"
              >
                {exclusionLoading ? '⏳ Processing...' : '🙈 Confirm Hide'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Access Request Modal */}
      {showImageAccessModal && (
        <ImageAccessRequestModal
          isOpen={showImageAccessModal}
          onClose={() => {
            setShowImageAccessModal(false);
            setSelectedImageForAccess(null);
          }}
          ownerName={user.firstName}
          ownerUsername={username}
          imageCount={user.images?.length || 0}
          requestType={selectedImageForAccess?.hasAccess ? 'renewal' : 'initial'}
          onSubmit={handleSubmitAccessRequest}
        />
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <MessageModal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          profile={user}
        />
      )}

      {/* PII Request Modal */}
      <PIIRequestModal
        isOpen={showPIIRequestModal}
        profileUsername={username}
        profileName={`${user.firstName} ${user.lastName}`}
        currentAccess={piiAccess}
        requestStatus={piiRequestStatus}
        visibilitySettings={{
          contactNumberVisible: user.contactNumberVisible,
          contactEmailVisible: user.contactEmailVisible,
          linkedinUrlVisible: user.linkedinUrlVisible
        }}
        requesterProfile={currentUserProfile}
        targetProfile={user}
        onClose={() => setShowPIIRequestModal(false)}
        onRefresh={() => {
          logger.debug('PIIRequestModal refresh');
          checkPIIAccess(); // Refresh access status when modal opens
        }}
        onSuccess={() => {
          setSuccessMessage('Request sent successfully!');
          setTimeout(() => setSuccessMessage(''), 5000); // Auto-hide after 5 seconds
          checkPIIAccess(); // Refresh access status after submit
        }}
      />

      {/* Image Lightbox Modal — Facebook-style with zoom/pan/navigation */}
      {showLightbox && lightboxImage && (
        <div
          className="lb-overlay"
          onClick={closeLightbox}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight' && lightboxImages.length > 1) lightboxNavigate(1);
            if (e.key === 'ArrowLeft'  && lightboxImages.length > 1) lightboxNavigate(-1);
            if (e.key === '+' || e.key === '=') lightboxZoomIn();
            if (e.key === '-') lightboxZoomOut();
          }}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          {/* Top-left: close */}
          <button
            className="lb-btn lb-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            title="Close (Esc)"
          >
            ✕
          </button>

          {/* Top-right: zoom controls */}
          <div className="lb-zoom-controls" onClick={(e) => e.stopPropagation()}>
            <button
              className="lb-btn lb-zoom-in"
              onClick={lightboxZoomIn}
              disabled={lightboxZoom >= 5}
              title="Zoom in (+)"
            >
              +
            </button>
            <span className="lb-zoom-level">{Math.round(lightboxZoom * 100)}%</span>
            <button
              className="lb-btn lb-zoom-out"
              onClick={lightboxZoomOut}
              disabled={lightboxZoom <= 1}
              title="Zoom out (−)"
            >
              −
            </button>
          </div>

          {/* Photo counter */}
          {lightboxImages.length > 1 && (
            <div className="lb-counter" onClick={(e) => e.stopPropagation()}>
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
          )}

          {/* Left nav */}
          {lightboxImages.length > 1 && (
            <button
              className="lb-btn lb-nav lb-prev"
              onClick={(e) => { e.stopPropagation(); lightboxNavigate(-1); }}
              title="Previous (←)"
            >
              ‹
            </button>
          )}

          {/* Image */}
          <div
            className="lb-image-wrap"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (lightboxZoom <= 1) return;
              setLightboxDragging(true);
              lightboxDragStart.current = { x: e.clientX, y: e.clientY };
              lightboxPanStart.current = { ...lightboxPan };
            }}
            onMouseMove={(e) => {
              if (!lightboxDragging) return;
              const dx = e.clientX - lightboxDragStart.current.x;
              const dy = e.clientY - lightboxDragStart.current.y;
              setLightboxPan({ x: lightboxPanStart.current.x + dx, y: lightboxPanStart.current.y + dy });
            }}
            onMouseUp={() => setLightboxDragging(false)}
            onMouseLeave={() => setLightboxDragging(false)}
            style={{ cursor: lightboxZoom > 1 ? (lightboxDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={getAuthenticatedImageUrl(lightboxImage)}
              alt={`${user?.firstName || 'User'} ${lightboxIndex + 1}`}
              className="lb-image"
              style={{
                transform: `scale(${lightboxZoom}) translate(${lightboxPan.x / lightboxZoom}px, ${lightboxPan.y / lightboxZoom}px)`,
                transition: lightboxDragging ? 'none' : 'transform 0.2s ease',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
              draggable={false}
            />
          </div>

          {/* Right nav */}
          {lightboxImages.length > 1 && (
            <button
              className="lb-btn lb-nav lb-next"
              onClick={(e) => { e.stopPropagation(); lightboxNavigate(1); }}
              title="Next (→)"
            >
              ›
            </button>
          )}

          {/* Thumbnail strip */}
          {lightboxImages.length > 1 && (
            <div className="lb-thumbnails" onClick={(e) => e.stopPropagation()}>
              {lightboxImages.map((img, i) => (
                <img
                  key={i}
                  src={getAuthenticatedImageUrl(img)}
                  alt={`Thumbnail ${i + 1}`}
                  className={`lb-thumb ${i === lightboxIndex ? 'active' : ''}`}
                  onClick={() => {
                    lightboxIndexRef.current = i;
                    setLightboxIndex(i);
                    setLightboxImage(img);
                    setLightboxZoom(1);
                    setLightboxPan({ x: 0, y: 0 });
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity Summary Panel - Admin only */}
      {showActivityPanel && user?.username && (
        <ActivitySummaryPanel
          username={user.username}
          onClose={() => setShowActivityPanel(false)}
        />
      )}
    </div>
  );
};

export default Profile;
